import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MutableRefObject, type ReactNode } from 'react';
import { api } from './lib/api';
import { splitIntoLines } from './lib/sentenceSplit';
import { IconBack, IconClose, IconCheck } from './components/icons';
import { StagedLoading } from './components/StagedLoading';
import { ErrorCard } from './components/ErrorCard';
import { CvFullDrawer } from './components/CvFullDrawer';
import { annotateTitles } from './lib/titleAnnotations';
import { isQuantityAsk, QUANTITY_HINT_COPY } from './quantityHint';
// R5.7b fix (Claude+Sol adjudicated under owner delegation 2026-08-22) — three frozen chrome strings misdescribed the
// new engine's NEUTRAL prompts ("Sharpen", "N points to review"); each is now a REGISTERED string at the same slot,
// a copy change only, no layout change (codebook Appendix A.5-P.14).
import { COPY } from './rubric/copy';

// Polish B2 reliability tranche — the quality read's own staged wait (first load + re-run), replacing the flat
// "Reading your entries…" line with the honest, timer-advanced, holds-at-final-stage pattern used everywhere else.
const QUALITY_STAGES = [
  'Reading your entries for quality — impact, outcomes, specifics…',
  'Weighing each section against the standard…',
  'Still reading — a full CV takes a minute…',
];

// EVAL-SURFACE REDESIGN (ratified: owner + Claude + Sol). The analysis is unchanged — the PRESENTATION is restructured
// into three levels, because the single dense wall was the actual problem:
//   L1 OVERVIEW  — one screen: what's strong, what needs attention, what to do first. Compact section cards in the
//                  CV's OWN DOCUMENT ORDER (the overview reads top-to-down like the document it describes), a
//                  "Start here" affordance on the most urgent card — which no longer MOVES that card — the specialty
//                  read as a CHIP ROW, IMG as a one-line strip (ImgPanel). Plain language only — CanMEDS / "impact
//                  framing" / "credibility" live below. "Review this section · N items" is the PRIMARY affordance.
//   L2 SECTION   — ROW-ALIGNED TWO-COLUMN GRID: each FLAGGED bullet is ONE ROW — the user's own bullet on the left,
//                  that bullet's diagnosis + question + the improve affordance on the right, TOP-ALIGNED on a shared
//                  row edge. ALL rows are visible at once: no toggling, no one-open-at-a-time, so the correspondence
//                  is persistent and there is nothing to track. Unflagged bullets render LEFT-CELL-ONLY at reduced
//                  emphasis. Next/Previous survive only as QUIET scroll shortcuts. On <700px the columns stack —
//                  every feedback block directly beneath its own bullet — and every row still renders.
//   L3 DETAIL    — on-demand expanders: methodology, the section standard, marker/term definitions.
//
// NUMERICS POLICY (owner-ruled, strict): NO fraction/denominator counts render anywhere user-facing, at ANY level.
// Exceptions: integrity/disclosure counts that are actionable obligations. Small-sample notices are QUALITATIVE.
// Traceability is by POINTING at the exact bullets (the gutter markers + the chip anchors), never by counting them.
//
// FIREWALL (absolute, unchanged): the system DIAGNOSES + GUIDES; the USER writes every word.
//  - the bullet text is shown READ-ONLY (the user's own words; display only — no write/compose path).
//  - the memory-jog chips JOG memory (they surface a QUESTION) — they NEVER write/append/compose bullet text.
//  - the editable field (revealed on "Improve") holds the user's own bullet; Save routes through the existing
//    PATCH /api/bullets/:id (the user-author writer). There is NO template-append, NO sentence-builder, NO system prose.
//  - the ANTI-FABRICATION warning renders ONCE per section at the TOP of L2 (prominent, beside the Do-this-first
//    banner) AND again directly beside any improve editor while that editor is OPEN — so it is on screen whenever
//    the user is actually drafting. (Ratified relaxation: with every row visible at once, repeating it beneath every
//    CLOSED affordance printed the same warning down the whole page.) The COPY is byte-identical.

export type Rating = 'strong' | 'adequate' | 'needs-attention';
export interface QWeakBullet { bullet_id: string; bullet_text: string; gate_id: string; diagnosis: string; question: string; weak_lines?: number[] }
// L2 point numbering (owner regression, 2026-08-08) — the SERVE-TIME display enrichment (workers enrichReadSiblings):
// every live bullet of a flagged entry with its 1-based ABSOLUTE position, so the left column can show ALL points of
// the role — clean siblings in black, flagged in red, numbered by true position. Display context only, never a finding.
// Optional: a stored read served without enrichment (or a failed enrichment) falls back to compressed numbering.
export interface QSiblingBullet { bullet_id: string; bullet_text: string; position: number; flagged: boolean }
// A QItem is ENTRY-grained — one role; `bullets` holds the role's reviewed bullets, each shown text-first.
// `entry_organization` is the entry's OWN organization (server-side, from the entries table — never model output).
// OPTIONAL: absent when blank, when it IS the title, and in every read persisted before org-qualified headings.
export interface QItem { entry_id: string; entry_title: string; entry_organization?: string; gate_id: string; bullets: QWeakBullet[]; all_bullets?: QSiblingBullet[] }
// A memory-jog chip: a short component label + the diagnosis-aware micro-question it surfaces (what a strong version
// of that component needs). The question is GUIDANCE the tool asks — never written into the bullet.
export interface PromptChip { chip: string; question: string }
// The grounded "what a strong entry shows" model for the section (CanMEDS-named, action-verb-led, placeholders only).
export interface QStandard { section: string; canmeds: string | null; components: string[]; exemplar: string; prompts: PromptChip[] }
// The section-level COVERAGE assessment (the framework read) + the situation-aware recommendation — server-templated.
// C1: `tier` is nullable — no baseline row means no specialty/emphasis claim (never fails open to "standard").
export interface QAssessment { canmeds: string | null; tier: number | null; tagged: number; entries: number; line: string; roleFrame: string }
// A strong entry shown AFFIRMINGLY in a strong section — the user's own title + bullets, read-only, no critique.
export interface QStrongEntry { entry_id: string; entry_title: string; entry_organization?: string; bullets: { bullet_id: string; bullet_text: string }[]; markers?: string[] }
// C1 — the DISCLOSED arithmetic behind the section verdict. REDESIGN: the ratio itself is no longer RENDERED (numerics
// policy) — the fields still arrive and still drive which QUALITATIVE sentence is shown.
export interface QEvidence {
  assessedBullets: number; flaggedBullets: number;
  assessedEntries: number; weakEntries: number;
  byGate: Record<string, number>;
  integrityFlags: number; integrityByGate: Record<string, number>;
  limitedSample: boolean;
  evidenceMarkers?: Record<string, number>;
  unflaggedWithoutEvidence?: number;
}
// FIX 1 (Sol #2) — an AUTOMATED ACCURACY CAUTION. The read used to COUNT integrity flags and give them top urgency
// while DISCARDING their diagnosis + question, so the user was accused of something invisible and unactionable
// ("hidden-but-counted is the worst state"). Each caution now carries its own bullet, the hedged diagnosis, and the
// question — rendered as a CHECK TO CONFIRM, never as an adjudicated finding.
// `urgency`: 'verify' = a concrete status/venue discrepancy (may elevate the card); 'confirm' = a contribution/framing
// reading, which is calm pre-send verification and NEVER elevates. Optional on a legacy stored read → nothing renders.
export type IntegrityUrgency = 'verify' | 'confirm';
// WHICH CLAIM a caution is about. 'bullet' is the per-bullet classifier's verdict. The two ENTRY targets come from the
// server's separate entry-level integrity pass, which adjudicates the entry's TITLE and ORGANIZATION as claims in
// their own right (an entry titled "Invited presentation" asserts something a program can check). Absent on a legacy
// stored read → treated as 'bullet', which is what every pre-existing caution was.
export type IntegrityTarget = 'bullet' | 'entry-title' | 'entry-organization';
export interface QIntegrityCaution {
  // The canonical finding id. The server guarantees exactly one terminal disposition per id, so this is also the
  // right dismissal key: an entry-target caution has NO bullet_id, and keying on (bullet_id, gate) would make two
  // different entries' title checks share one dismissal.
  id?: string;
  target?: IntegrityTarget;
  entry_id: string; entry_title: string; entry_organization?: string;
  // The CLAIM TEXT this caution renders beside, read-only: the user's own BULLET for a 'bullet' target, and the
  // entry's own TITLE / ORGANIZATION for the entry targets. `bullet_id` is '' for an entry target.
  bullet_id: string; bullet_text: string;
  evidence?: string;
  gate_id: string; diagnosis: string; question: string; urgency: IntegrityUrgency;
}
// The stable per-reader dismissal identity. Prefers the server's canonical finding id; falls back to the historical
// (bullet, gate) pair for a legacy stored read that predates ids.
function cautionKey(c: QIntegrityCaution): string { return c.id || `${c.bullet_id}::integrity:${c.gate_id}`; }
function isEntryTarget(c: QIntegrityCaution): boolean { return c.target === 'entry-title' || c.target === 'entry-organization'; }
export interface SectionQuality { section: string; rating: Rating; bulletsRating?: Rating; total: number; items: QItem[]; standard?: QStandard; assessment?: QAssessment; recommendation?: string; evidence?: QEvidence; strongEntries?: QStrongEntry[]; integrityCautions?: QIntegrityCaution[];
  // FIX 2 (efficacy re-run) — flagged ENTRIES this section is withholding behind the server's ≤3 display cap.
  // Absent/0 when nothing is withheld; absent on a legacy stored read (which then renders exactly as before).
  suppressedItems?: number;
  // FIX 3 (Sol #4) — the server's QUALITATIVE state for a section too small to carry a density rating (1–2 bullets).
  // When present it REPLACES the categorical headline: a 2-bullet section must not read "Needs sharpening" off one
  // flag. Absent on n ≥ 3 and on legacy stored reads → the categorical verdict renders exactly as before.
  smallSectionState?: string }
/** FIX 2 (Sol ruling (a)) — the DISPLAY-CAP DISCLOSURE, in Sol's ratified wording. Rendered at BOTH L1 and L2 so the
 *  fact is present wherever the user counts what is left. Sol ruled a simple COUNT acceptable here ("it is
 *  navigation, not metric noise"); the owner's strict numerics ruling still bans fractions/denominators, so this is a
 *  bare cardinality with no total beside it. Renders nothing at 0/absent — a legacy read is unchanged. */
function CapDisclosure({ n, tid }: { n: number; tid: string }) {
  if (!n || n <= 0) return null;
  return (
    <p style={S.editorial} data-testid={`quality-cap-disclosure-${tid}`}>
      {n === 1 ? '1 more entry in this section needs review.' : `${n} more entries in this section need review.`}
    </p>
  );
}
// C5 — one consolidated "couldn't be read" entry, naming WHICH entry (the server already carries the label).
export interface QUnreadable { section: string; title: string }
// C5 — the deterministic entry-length editorial note (>6 rendered points). NOT a rating input.
export interface QEditorial { entry_id: string; entry_title: string; entry_organization?: string; section: string; points: number }
// Specialty Depth v1 — the "[Specialty] relevance" panel's shape (server: buildSpecialtyRelevance).
export interface SpecialtyRelevanceSignal {
  id: string; shown: boolean; entry_ids: string[]; hidden_entry_ids: string[];
  plain_language: string; what_counts: string; recover_question: string;
  provenance: { claim_kind: string; source: string };
}
export interface SpecialtyRelevance {
  specialty: string; signal_set_version: string; detector_version: string;
  // `organization` is OPTIONAL — reads persisted before org-disambiguation carry {id, title} only.
  signals: SpecialtyRelevanceSignal[]; entry_titles: { id: string; title: string; organization?: string }[];
}
// FIX 1 (re-probe) — a CREDENTIAL-section accuracy caution, for the read-level "Confirm before you send" panel. Same
// shape as the experience caution plus its own section label (a credential section has NO SectionQuality — giving it
// one would hand it a quality verdict, which is exactly the category error the presence-only rule prevents).
export interface QCredentialCaution extends QIntegrityCaution { section: string }
export interface QualityRead { lead: string; overall: string; sections: SectionQuality[]; canmedsCap?: string; unreadable?: QUnreadable[]; editorial?: QEditorial[]; specialty_relevance?: SpecialtyRelevance;
  // Server-ordered: `verify` band first, then `confirm`. Absent on a legacy stored read → the panel renders nothing.
  credentialCautions?: QCredentialCaution[] }
export interface ParseQuality { poor: boolean; experience_total: number; experience_with_content: number }

// Client-side plain-language phrase for each impact gate — mirrors the server's GATE_PHRASE without exposing the
// gate id itself to the user. REDESIGN: rendered WITHOUT its count (numerics policy).
const GATE_PHRASE_CLIENT: Record<string, string> = {
  DUTIES_NOT_IMPACT: 'contribution not shown',
  NO_QUANTIFICATION: 'outcomes stated without specifics',
  TITLE_WITHOUT_ACTION: 'title without the action',
};
// C3b — plain-language phrase per evidence-vocabulary token, mirroring the server's MARKER_PHRASE (qualityRead.ts).
const MARKER_PHRASE_CLIENT: Record<string, string> = {
  scope: 'scope', setting: 'the setting', responsibility: 'level of responsibility', feedback: 'feedback received',
  qualitative_outcome: 'a qualitative outcome', metric: 'a metric', method: 'the method', finding: 'the finding',
  audience: 'who it reached', takeaway: 'what they took away',
};

// The QUALITY (bullets) axis word — DETAIL-LAYER vocabulary (L3), kept distinct from the coverage word.
const QUALITY_WORD: Record<Rating, string> = { strong: 'strong', adequate: 'solid', 'needs-attention': 'need sharpening' };
// L1 PLAIN-LANGUAGE verdict — the first thing the user reads. No framework vocabulary, no adjective-without-basis.
// COPY CONTRACT (owner-endorsed, Sol-ratified, 2026-08-07): speak to the applicant, and CLAIM ONLY WHAT THE CHECK
// ESTABLISHED. See the two notes below — both old strings failed the second half.
const PLAIN_VERDICT: Record<Rating, string> = {
  // HONESTY FIX — the old "nothing here needs work first" OVERCLAIMED: it asserted the section is good, when all the
  // check established is that nothing was FLAGGED. Our own ratified contract says "clearing the checks is not proof
  // that a bullet is strong", and this card was violating it.
  // FURTHER TIGHTENING (Sol-ratified 2026-08-07) — the "Reads strongly" opener is dropped entirely. Sol: "'Reads
  // strongly' is not defensible: a low weak-density rate is not an explicit positive rubric." The rendered rating
  // is bulletsRating ?? rating; a 'strong' bullets rating is merely a low weakRate, not a satisfied positive rubric.
  strong: 'This check did not find an entry to prioritise.',
  adequate: 'Mostly solid — a few entries would read stronger.',
  // "SEVERAL", NOT "MOST" — deliberate, and load-bearing. The rating rendered here is `bulletsRating ?? rating`, and
  // bulletsRating reaches 'needs-attention' at `weakRate >= DENSITY_NEEDS_ATTENTION` where DENSITY_NEEDS_ATTENTION =
  // 0.5 (workers/src/lib/qualityRead.ts), with NO MIN_SAMPLE guard on that axis. So this verdict fires at EXACTLY
  // half (and on 1-of-2). "Most entries" would be a count the arithmetic does not support. If the threshold is ever
  // raised genuinely ABOVE half, "most" becomes sayable again — not before.
  'needs-attention': 'Needs sharpening — several entries say what the role involved, but not what you specifically did.',
};
// The ANTI-FABRICATION warning. MUST-NOT-LOSE: it renders BESIDE EVERY improve/suggestion affordance, never once.
const ANTI_FABRICATION = 'Use outcomes only where you genuinely measured or observed them — never invent numbers or imply an improvement you did not see.';

// C4 — the coverage word derives from CONFIRMED TAGS ONLY, never a raw entry count. REDESIGN (mechanical fix 3): the
// zero-tag state is no longer the ambiguous "not yet assessed" — it says plainly that specialty coverage does not
// apply here, and the caller renders one plain sentence of WHY.
function coverageWord(a?: QAssessment): string | null {
  if (!a) return null;
  const tagged = a.tagged ?? 0;
  if (tagged >= 2) return `confirmed on ${tagged} entries`;
  if (tagged === 1) return 'confirmed on 1 entry';
  return 'not applicable';
}
function ratingInk(r: Rating): string {
  return r === 'strong' ? 'var(--success, #1E7A3D)' : r === 'needs-attention' ? 'var(--warning, #8A5A00)' : 'var(--muted)';
}

// FIX 1 — the client mirror of the server's INTEGRITY_URGENCY map. 'verify' gates assert something concrete and
// checkable about STATUS or VENUE; 'confirm' gates are precision-prone readings of contribution/framing. An unmapped
// (future) gate defaults to the CALM band — a check we have not reasoned about never gets to shout.
const INTEGRITY_URGENCY_CLIENT: Record<string, IntegrityUrgency> = {
  STATUS_MISREPRESENTATION: 'verify',
  VENUE_INFLATION: 'verify',
  CONTRIBUTION_CLARITY: 'confirm',
  RESEARCH_ROLE_VAGUE: 'confirm',
  SAVIOR_FRAMING: 'confirm',
};
function urgencyOfGate(gate_id: string): IntegrityUrgency { return INTEGRITY_URGENCY_CLIENT[gate_id] ?? 'confirm'; }
// How many of a section's accuracy checks fall in each band. Reads the CAUTIONS when they are present, and degrades to
// the disclosed per-gate counts for a legacy stored read that predates them — so an old read is banded correctly too.
function cautionCounts(s: SectionQuality): { verify: number; confirm: number } {
  const cautions = s.integrityCautions;
  if (cautions && cautions.length > 0) {
    return {
      verify: cautions.filter((c) => c.urgency === 'verify').length,
      confirm: cautions.filter((c) => c.urgency !== 'verify').length,
    };
  }
  let verify = 0, confirm = 0;
  for (const [gate, n] of Object.entries(s.evidence?.integrityByGate ?? {})) {
    if (urgencyOfGate(gate) === 'verify') verify += n; else confirm += n;
  }
  return { verify, confirm };
}

// L1 URGENCY. REDESIGN: urgency no longer REORDERS the cards — the overview renders in the CV's own DOCUMENT ORDER,
// because the owner reviews top-to-down and the review must follow the document. Urgency now only decides WHERE THE
// "Start here" BADGE LANDS: the badge marks the most urgent card in place.
// FIX 1 — an accuracy check may claim rank 0 ONLY when it is a 'verify' gate (a concrete status/venue discrepancy the
// user can check and correct). A 'confirm' gate — contribution/framing — is precision-prone, so it belongs in the calm
// pre-send grouping and does NOT move a card's rank at all. This is what stops every hedged caution from shouting
// "Start here".
function urgencyRank(s: SectionQuality): number {
  if (cautionCounts(s).verify > 0) return 0;
  // FIX 3 — "Start here" cannot be claimed on DENSITY alone below three bullets: one flag on two bullets is not a
  // stronger signal than a genuinely dense section. Such a section floors at the calm rank. A 'verify' accuracy check
  // still elevates it above (checked first) — that is a factual discrepancy, not a density reading.
  if (s.smallSectionState) return 2;
  const r = s.bulletsRating ?? s.rating;
  if (r === 'needs-attention') return 1;
  if (r === 'adequate') return 2;
  return 3;
}

// FIX 1 — DISMISSAL. DESIGN choice (reported): client-side localStorage, not a server PATCH. The relevance-hide
// precedent PATCHes because it corrects a SERVER-COMPUTED LINK that must survive into the next read; a dismissal is a
// per-reader "I checked this wording" acknowledgement with no server consumer, so a server path would have cost a new
// endpoint plus a migration for no behavioural gain. Keyed by CV + BULLET + GATE, so dismissing one check never
// silences the same gate on another bullet — and it never suppresses the disclosed COUNT.
// Keyed by CV + the caution's canonical FINDING ID, so dismissing one check never silences the same gate on another
// bullet OR on another entry's title — and it never suppresses the disclosed COUNT.
function dismissKey(cvId: string, key: string): string {
  return `cvc.dismissed-check.${cvId}.${key}`;
}
// The pre-id key shape, still READ (never written) so checks a user already dismissed do not all reappear once on the
// first read after this change. Only meaningful for bullet targets — entry targets did not exist before ids.
function legacyDismissKey(cvId: string, bulletId: string, gateId: string): string {
  return `cvc.dismissed-check.${cvId}.${bulletId}.${gateId}`;
}
function readDismissed(cvId: string, c: QIntegrityCaution): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    if (localStorage.getItem(dismissKey(cvId, cautionKey(c))) === '1') return true;
    return !!c.bullet_id && localStorage.getItem(legacyDismissKey(cvId, c.bullet_id, c.gate_id)) === '1';
  } catch { return false; }
}
function writeDismissed(cvId: string, c: QIntegrityCaution): void {
  try { localStorage?.setItem(dismissKey(cvId, cautionKey(c)), '1'); } catch { /* storage unavailable → the check simply stays visible */ }
}
// The index of the card that carries "Start here" — the most urgent section, ties broken by document order (the
// earliest such section wins). Returns -1 when nothing is urgent enough to point at (everything solid or strong).
function startHereIndex(sections: SectionQuality[]): number {
  let best = -1;
  let bestRank = Infinity;
  sections.forEach((s, i) => {
    const r = urgencyRank(s);
    if (r <= 1 && r < bestRank) { best = i; bestRank = r; }
  });
  return best;
}

// Viewport hook — mirrors CvWorkspace/App's useIsNarrow (matchMedia guarded for jsdom). L2's two columns STACK below
// this width; every row still renders (one-at-a-time never returns on any viewport).
const L2_STACK_WIDTH = 700;
// SHOW-ALL (owner ruling 2026-08-08) — the server ships EVERY flagged entry; the first this-many render fully
// expanded (exactly the pre-lift view), the rest render collapsed-in-place until the user opens them.
// THE LEGACY DEFAULT, UNCHANGED: the first three entries render expanded and the rest behind a disclosure
// (owner ruling 2026-08-08). The RUBRIC surface overrides it to 0 — see `collapseAfter` on `QualityReadBody`.
const COLLAPSE_AFTER = 3;
function useIsNarrow(maxWidth: number): boolean {
  const mqOf = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(`(max-width: ${maxWidth}px)`)
      : null;
  const [narrow, setNarrow] = useState(() => mqOf()?.matches ?? false);
  useEffect(() => {
    const mq = mqOf();
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, [maxWidth]);
  return narrow;
}

// prefers-reduced-motion, guarded for jsdom. A user who asked for less motion gets an instant jump, not a smooth one.
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

export function QualityReadView({ cvId, specialtyName, onCraft, onHome, onGatedChange, onPaywall, reloadKey = 0 }: {
  cvId: string; specialtyName: string; onCraft: () => void; onHome: () => void;
  onGatedChange?: (gated: boolean) => void;
  onPaywall?: (cvId: string) => void;
  reloadKey?: number;
}) {
  const [read, setRead] = useState<QualityRead | null>(null);
  const [failed, setFailed] = useState(false);
  const [failedRetrySafe, setFailedRetrySafe] = useState(false);
  // WHY the load failed (Sol round-3 #2): a failed REFRESH must be retried AS a refresh — the server only honors the
  // grace_recompute marker (an unpaid CV whose read the migration erased) on ?refresh=1, so a plain retry would
  // paywall the very user the marker exists to recover. A failed plain first-load retries plain.
  const [failedWasRefresh, setFailedWasRefresh] = useState(false);
  const [locked, setLocked] = useState(false);   // the recompute 402'd (unpaid) AND there is no stored read to show
  const [stale, setStale] = useState(false);     // the stored read describes pre-edit content (offer a re-run)
  // How many entries changed/were added/were deleted since the stored read. A DETAIL on the stale banner — the
  // server's whole-CV hash remains the authoritative staleness gate. NUMERICS POLICY: a simple cardinality only;
  // no fraction, no denominator, no total beside it. Null = the server had nothing to compare → no number shown.
  const [changedEntries, setChangedEntries] = useState<number | null>(null);
  const [assessedAt, setAssessedAt] = useState<number | null>(null);
  const [rerunning, setRerunning] = useState(false);
  const [parseQuality, setParseQuality] = useState<ParseQuality | null>(null);
  const [proceedAnyway, setProceedAnyway] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  // L1 ⇄ L2: the section currently under review (its label), or null for the overview.
  const [focusSection, setFocusSection] = useState<string | null>(null);
  // Re-run VISIBLE ACTIVITY (owner: "clicking the rerun does not indicate it is doing so visually"). The brief note
  // shown after a successful re-run, and the polite live-region text. The continuous animation is NEVER announced.
  const [justUpdated, setJustUpdated] = useState(false);
  const [liveMsg, setLiveMsg] = useState('');

  const load = async (refresh: boolean, force = false) => {
    if (refresh) { setRerunning(true); setJustUpdated(false); setLiveMsg('Re-running your review'); }
    setLocked(false);
    const qs = [refresh ? 'refresh=1' : '', force ? 'force=1' : ''].filter(Boolean).join('&');
    const r = await api<{ read?: QualityRead; stale?: boolean; changed_entries?: number | null; assessed_at?: number | null; parse_quality?: ParseQuality; parse_recovery?: boolean; error?: string; reason?: string; retry_safe?: boolean }>(`/api/coverage/cv/${cvId}/quality${qs ? `?${qs}` : ''}`);
    if (refresh) setRerunning(false);
    if (r.status === 402 && r.data?.reason === 'needs_paid_cv') { setLocked(true); onPaywall?.(cvId); return; }
    if (r.ok && r.data?.parse_recovery) { setRecoveryMode(true); setParseQuality(r.data.parse_quality ?? null); setRead(null); setFailed(false); return; }
    setRecoveryMode(false);
    if (r.ok && r.data?.read) {
      // ATOMIC replacement — one commit, the fresh read swaps in whole (unchanged behaviour).
      setRead(r.data.read); setStale(!!r.data.stale); setChangedEntries(typeof r.data.changed_entries === 'number' ? r.data.changed_entries : null); setAssessedAt(r.data.assessed_at ?? null); setParseQuality(r.data.parse_quality ?? null); setFailed(false);
      setFocusSection(null); // a fresh read always lands on L1 — never inside a stale section detail
      if (refresh) { setJustUpdated(true); setLiveMsg('Review updated'); }
    } else {
      setFailed(true); setFailedRetrySafe(!!r.data?.retry_safe); setFailedWasRefresh(refresh);
      if (refresh) {
        // FAILED RE-RUN (Sol round-2 #4): NEVER keep rendering the pre-refresh read. The server may have committed
        // the atomic-bullet migration before the failure — the DB then holds new child bullets and a NULL stored
        // read, while this component's `read` still describes (and offers edit actions against) ARCHIVED bullet ids.
        // Clearing the read lands the UI in the honest "re-run to finish" failed state instead of a stale merged
        // view with dead ids. This clear is DELIBERATELY refresh-only (round-3 #2): a plain first-load failure has
        // nothing rendered to clear, and a failed refresh on an UNMIGRATED CV simply re-runs on Retry — the cleared
        // view never exposes edit actions, so no archived-id write can be issued from this state either way.
        setRead(null); setStale(false); setChangedEntries(null); setFocusSection(null);
        setLiveMsg('');   // FAILURE: the ErrorCard below carries the message; nothing false is announced
      }
    }
  };
  useEffect(() => { void load(false); }, [cvId, reloadKey]);

  // "Updated just now" fades on its own; the next interaction (opening a section) clears it too.
  useEffect(() => {
    if (!justUpdated) return;
    const t = setTimeout(() => setJustUpdated(false), 6000);
    return () => clearTimeout(t);
  }, [justUpdated]);

  const gated = !!parseQuality?.poor && !proceedAnyway;
  useEffect(() => { onGatedChange?.(gated); }, [gated, onGatedChange]);

  // LOCKED (recoverable): the recompute 402'd and there's no stored read to fall back on.
  if (!read && locked) {
    return (
      <main style={S.main} data-testid="quality-read">
        <button className="btn btn-ghost" style={S.back} onClick={onHome} data-testid="quality-home"><IconBack aria-hidden="true" /> Back to home</button>
        <div style={S.parseWarn} data-testid="quality-locked">
          <h2 style={S.parseWarnTitle}>Unlock this CV to see its review</h2>
          <p style={{ margin: '0 0 var(--space-4)' }}>
            Your first review is free. To re-evaluate this CV — and to craft, tailor, and export it — unlock it.
          </p>
          <button className="btn btn-primary" onClick={() => onPaywall?.(cvId)} data-testid="quality-locked-unlock">Unlock this CV</button>
        </div>
      </main>
    );
  }

  // C2 — RECOVERY MODE: the server didn't compute a read at all (parse preflight, before the LLM/free-spend).
  // NUMERICS POLICY: the old "Only 3 of 10 of your entries came through" fraction is gone — the steer is qualitative
  // and still names the actionable obligation (re-import), which is what the user can actually do something about.
  if (recoveryMode && parseQuality) {
    return (
      <main style={S.main} data-testid="quality-read">
        <button className="btn btn-ghost" style={S.back} onClick={onHome} data-testid="quality-home"><IconBack aria-hidden="true" /> Back to home</button>
        <div style={S.parseWarn} data-testid="quality-recovery">
          <h2 style={S.parseWarnTitle}>I couldn’t cleanly separate this CV into entries</h2>
          <p style={{ margin: '0 0 var(--space-3)' }}>
            Most of your experience entries came through without their content — so a read now would be incomplete.
            This usually happens with <strong>two-column layouts</strong> or text pasted from a <strong>PDF</strong>,
            where the columns get interleaved.
          </p>
          <p style={{ margin: '0 0 var(--space-3)' }}>To get a complete read, try re-importing your CV <strong>as a single column</strong>, or pasting it <strong>section by section</strong>.</p>
          <p style={{ margin: '0 0 var(--space-4)', color: 'var(--muted, #777)' }}>Assessing a poorly-read CV will use your free evaluation — re-importing first is usually better.</p>
          <button className="btn btn-secondary" onClick={() => void load(false, true)} data-testid="quality-assess-anyway">Assess it anyway <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></button>
        </div>
      </main>
    );
  }

  if (!read && !failed) {
    return (
      <main style={S.main} data-testid="quality-read">
        <StagedLoading stages={QUALITY_STAGES} intervalMs={6000} testId="quality-loading" subtext={null} />
      </main>
    );
  }

  return (
    <QualityReadBody
      cvId={cvId}
      specialtyName={specialtyName}
      read={read}
      onCraft={onCraft}
      onHome={onHome}
      onPaywall={onPaywall}
      rerunning={rerunning}
      onRerun={() => void load(true)}
      assessedAt={assessedAt}
      stale={stale}
      changedEntries={changedEntries}
      justUpdated={justUpdated}
      onInteract={() => setJustUpdated(false)}
      liveMsg={liveMsg}
      failed={failed}
      failedRetrySafe={failedRetrySafe}
      onRetry={failedRetrySafe ? () => load(failedWasRefresh) : undefined}
      parseQuality={parseQuality}
      proceedAnyway={proceedAnyway}
      onProceedAnyway={() => setProceedAnyway(true)}
      focusSection={focusSection}
      onFocusSection={setFocusSection}
      onSaveBullet={saveBulletLegacy}
      onHideRelevance={(signalId, entryId) => hideRelevanceLegacy(cvId, signalId, entryId)}
    />
  );
}

// R5.7b — THE LEGACY READ'S OWN WRITE PATHS, lifted out of the presentational body so the body can be rendered by
// another data source (the rubric engine, through `rubric/legacyAdapter.ts`) WITHOUT inheriting endpoints that do
// not exist for it. Byte-identical requests: the bullet save is the user-author writer (`PATCH /api/bullets/:id`,
// the user's typed text ONLY), the relevance hide is the server-computed-link correction.
async function saveBulletLegacy(bulletId: string, text: string): Promise<{ ok: boolean; status: number }> {
  // FIREWALL: send ONLY the user's typed text — never any composed/appended string.
  const r = await api(`/api/bullets/${bulletId}`, { method: 'PATCH', body: JSON.stringify({ text }) });
  return { ok: r.ok, status: r.status };
}
async function hideRelevanceLegacy(cvId: string, signalId: string, entryId: string): Promise<{ ok: boolean; shown?: boolean } | null> {
  const r = await api<{ ok?: boolean; shown?: boolean }>(`/api/coverage/cv/${cvId}/relevance-hide`, {
    method: 'POST', body: JSON.stringify({ signal_id: signalId, entry_id: entryId }),
  });
  if (!r.ok) return null;
  return { ok: true, shown: r.data.shown };
}

/**
 * R5.7b — `QualityReadBody`: the PRESENTATIONAL body of the legacy read (owner directive, 2026-08-22: "we iterated
 * a lot to arrive at the current UI; copy it AS IS — the change is the content from the engine, not the display").
 *
 * This is `QualityReadView`'s L1 overview + L2 section review, EXACTLY as they were — same components, same JSX,
 * same styles, same placements — with the fetch, the loading/locked/recovery branches and the two write paths moved
 * OUT to the caller. `QualityReadView` still renders it against the legacy `/quality` payload; `rubric/RubricReadView`
 * renders it against the new engine's read, adapted into this data model by `rubric/legacyAdapter.ts`.
 *
 * THE THREE OPTIONAL HOOKS are the only additions, and each is ABSENT-SAFE (the legacy caller passes all of them,
 * so the legacy render is unchanged): `onSaveBullet` (without it the "Improve with prompts" affordance does not
 * render — an editor that could not save must not offer to), `onHideRelevance` (without it the per-entry hide
 * control does not render), and `entryFactsLink` (ONE text link per entry card, in the card's existing action area
 * after its rows, to that entry's facts on the facts screen — a link, not a block; R5.7b fix). `preamble` is
 * rendered first inside the page landmark (the rubric surface's intended-use statement, which the codebook pins
 * above the read at all times).
 */
export interface QualityReadBodyProps {
  cvId: string;
  specialtyName: string;
  read: QualityRead | null;
  onCraft: () => void;
  onHome: () => void;
  onPaywall?: (cvId: string) => void;
  rerunning: boolean;
  onRerun: () => void;
  assessedAt: number | null;
  stale: boolean;
  changedEntries: number | null;
  justUpdated: boolean;
  onInteract: () => void;
  liveMsg: string;
  failed: boolean;
  failedRetrySafe: boolean;
  onRetry?: () => Promise<void>;
  parseQuality: ParseQuality | null;
  proceedAnyway: boolean;
  onProceedAnyway: () => void;
  focusSection: string | null;
  onFocusSection: (section: string | null) => void;
  onSaveBullet?: SaveBullet;
  onHideRelevance?: HideRelevance;
  entryFactsLink?: EntryFactsLink;
  /** 2026-08-23 — the legacy drafting warning. `false` on the rubric surface: its rows are prompts, not a draft box. */
  antiFabrication?: boolean;
  /**
   * 2026-08-23 — how many entries render EXPANDED before the rest collapse (owner: within one section some entries
   * rendered as expanded blocks and the rest as collapsed "N points to check ▾" rows — "make the presentation
   * consistent"). The legacy default of 3 is right for legacy entries, which carry several bullets each and read
   * as blocks. On the rubric surface every entry carries ONE merged row, so three-expanded-then-collapsed made a
   * section's first three entries look like a different kind of thing from its fourth. That surface passes
   * `Infinity`: every entry is expanded and every line is a visible row, which is what the legacy read did and
   * what the row walk needs — a highlight cannot move to a row inside a collapsed entry.
   */
  collapseAfter?: number;
  /**
   * The "Re-run to add the {Specialty} relevance read" offer (the LEGACY specialty panel's upgrade CTA). It
   * promises a read ONLY the legacy `/quality` route can produce (`read.specialty_relevance`); a caller whose read
   * comes from the rubric engine — where the specialty panel is the rubric overlay, generic or specialty — passes
   * `false` so the page never offers a read it cannot add. `undefined` keeps the legacy behaviour byte-identical.
   */
  relevanceOffer?: boolean;
  preamble?: ReactNode;
  /** Registered notices the caller owes the page and the legacy view has no slot for — the overview's notice position, and above the section review (the notices describe the correction loop, whose controls live there). */
  notices?: ReactNode;
  /**
   * The three METHOD statements the legacy page hard-codes about the legacy engine (its intro paragraph, its scope
   * caveat, its L3 "what this review looks for / what the terms mean" layer). `undefined` renders the legacy text
   * byte-identically; a caller whose read comes from a different engine passes what is TRUE of it — or `null`,
   * which renders nothing in that slot rather than a sentence that misdescribes the read.
   */
  intro?: ReactNode | null;
  scopeNote?: ReactNode | null;
  methodology?: ReactNode | null;
}
export type SaveBullet = (bulletId: string, text: string) => Promise<{ ok: boolean; status: number }>;
export type HideRelevance = (signalId: string, entryId: string) => Promise<{ ok: boolean; shown?: boolean } | null>;
/**
 * R5.7b fix — THE ONE PER-ENTRY ADDITION a legacy entry card accepts: a TEXT LINK (never a block) in the card's
 * existing action area, after the entry's rows, to that entry's facts on the facts screen. `label` is the caller's
 * registered string (`CS-ENTRY-FACTS-LINK`); `hrefFor` returning null renders nothing; `onFollow` lets the caller
 * open and focus the target disclosure in-page.
 */
export interface EntryFactsLink {
  label: string;
  hrefFor: (entryId: string) => string | null;
  onFollow?: (entryId: string, e: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * 2026-08-23 (owner: "the left-right entry by entry review is what it is meant to be… other details could still
   * slot in, but that seems like a raw database").
   *
   * When supplied, the entry's facts render INLINE, inside one compact closed disclosure on the entry itself,
   * instead of as a link down to a whole-CV panel. That panel was the third stacked rendering of the same
   * entries: the applicant read the section index, then the entry rows, then every entry again as a fact card.
   * One reading layer, with the rest one click away ON the entry it belongs to.
   */
  detailsFor?: (entryId: string) => ReactNode;
  detailsLabel?: string;
}
function EntryFactsLinkLine({ link, entryId }: { link?: EntryFactsLink; entryId: string }) {
  if (!link) return null;
  // THE INLINE DISCLOSURE — preferred whenever the caller can render the entry's own details.
  if (link.detailsFor) {
    const body = link.detailsFor(entryId);
    if (body === null || body === undefined || body === false) return null;
    // The caller supplies its OWN disclosure (`EntryFactsDisclosure` already is one), so this wrapper adds a
    // container and nothing else — a second <details> around it would nest one summary inside another.
    return <div style={S.entryDetails} data-testid={`quality-entry-details-${entryId}`}>{body}</div>;
  }
  const href = link.hrefFor(entryId);
  if (href === null) return null;
  return (
    <p style={S.entryFactsLink}>
      <a href={href} data-testid={`quality-entry-facts-link-${entryId}`} onClick={(e) => link.onFollow?.(entryId, e)}>
        {link.label}
      </a>
    </p>
  );
}
/**
 * R5.7b — the gate-id prefix of the new engine's CLARIFICATION rows (flags and questions, `legacyAdapter.ts`). The
 * tag is attribute-only (it keys the section card's "Do this first" slot in the adapter); it does NOT change the
 * row's rendering. Sol's one-pass on the restored flow (owner: "original flow as is", 2026-08-23): a flagged row's
 * left-column point keeps the ORIGINAL treatment — the legacy warning colour — for adapted and legacy rows alike.
 */
export const CLARIFICATION_GATE_PREFIX = 'rubric_';

export function QualityReadBody({
  cvId, specialtyName, read, onCraft, onHome, onPaywall, rerunning, onRerun, assessedAt, stale, changedEntries,
  justUpdated, onInteract, liveMsg, failed, failedRetrySafe, onRetry, parseQuality, proceedAnyway, onProceedAnyway,
  focusSection, onFocusSection, onSaveBullet, onHideRelevance, entryFactsLink, relevanceOffer = true, preamble, notices,
  antiFabrication = true, collapseAfter = COLLAPSE_AFTER,
  intro, scopeNote, methodology,
}: QualityReadBodyProps) {
  // The full-CV left drawer (owner: "there should be a way to see the full original cv") — open at L1 AND L2.
  const [cvDrawerOpen, setCvDrawerOpen] = useState(false);

  const specLabel = specialtyName || 'your specialty';
  const sections = read?.sections ?? [];
  // DOCUMENT ORDER — the payload's own section order, untouched. The badge index is computed separately.
  const startAt = useMemo(() => startHereIndex(sections), [sections]);
  const focused = focusSection ? sections.find((s) => s.section === focusSection) ?? null : null;

  // L2 — the SECTION REVIEW screen. Rendered instead of the overview (one job on screen at a time).
  if (read && focused && (!parseQuality?.poor || proceedAnyway)) {
    // NO SECTION-TO-SECTION NAV. It was added on 2026-08-23 and it is not part of the legacy structure the owner
    // froze — "you have not implemented the old model fully. I thought copying it would solve the issues and
    // reproduce it as before." The legacy L2's Previous/Next moves the highlight between ROWS of this section;
    // moving between SECTIONS is what "Back to the overview" and the section cards are for. Removed rather than
    // kept as an extra, because an extra control is exactly the divergence being complained about.
    return (
      <main style={S.main} data-testid="quality-read">
        {preamble}
        <button className="btn btn-ghost" style={S.back} onClick={() => onFocusSection(null)} data-testid="quality-l2-back">
          <IconBack aria-hidden="true" /> Back to the overview
        </button>
        {notices}
        <SectionReview s={focused} cvId={cvId} onPaywall={onPaywall} stale={stale} onSaveBullet={onSaveBullet} entryFactsLink={entryFactsLink} antiFabrication={antiFabrication} collapseAfter={collapseAfter} />
        {/* the full-CV drawer is available at L2 too — and opens scrolled to the section under review */}
        <CvFullDrawer cvId={cvId} open={cvDrawerOpen} onOpen={() => setCvDrawerOpen(true)} onClose={() => setCvDrawerOpen(false)} focusSection={focused.section} />
      </main>
    );
  }

  const readShown = !!read && (!parseQuality?.poor || proceedAnyway);

  return (
    <main style={S.main} data-testid="quality-read">
      {preamble}
      <button className="btn btn-ghost" style={S.back} onClick={onHome} data-testid="quality-home"><IconBack aria-hidden="true" /> Back to home</button>

      {/* The two re-run motions, declared once (the inline-<style> idiom StagedLoading already uses). */}
      <style>{`@keyframes cvc-spin{to{transform:rotate(360deg)}}@keyframes cvc-indeterminate{0%{left:-40%}100%{left:100%}}`}</style>
      {/* Re-run activity, announced POLITELY once per transition. The animation itself is never announced. */}
      <p aria-live="polite" role="status" style={S.srOnly} data-testid="quality-live">{liveMsg}</p>

      {/* L1 — a NORMAL SINGLE COLUMN. The overview (h1, the ONE canonical re-run + timestamp, intro, scope caveat,
          summary, relevance offer, informational stale notice) reads top-to-bottom, then the section cards scroll
          beneath it. NOTHING is sticky here (Sol: "L1 is a normal single-column overview … do not make L1 cards
          sticky"). The page is the only scroll container. */}
      <h1>Your CV — a section-by-section read</h1>

      {/* TOP CONTROL — the ONE canonical re-run + the "Reviewed {when}" timestamp, kept at the TOP under the h1 (owner
          ask #1: they kept scrolling to the bottom to find it). This is the sole generic re-run affordance in the view;
          the footer re-run row is gone and the stale notice below is informational only. */}
      {readShown && (
        <div style={S.rerunRowTop} data-testid="quality-rerun-row">
          <span style={S.reviewedAt}>{assessedAt ? `Reviewed ${fmtWhen(assessedAt)}` : ''}</span>
          <button className="btn btn-ghost" style={S.rerunGhost} onClick={onRerun} disabled={rerunning} data-testid="quality-rerun-always">
            {rerunning ? <><Spinner /> Re-running…</> : '↻ Re-run review'}
          </button>
        </div>
      )}
      {/* On completion: a brief note beside the control. Fades on its own; cleared by the next interaction. */}
      {justUpdated && !rerunning && (
        <p style={S.updatedNote} data-testid="quality-updated-note">Updated just now</p>
      )}

      {intro === undefined ? (
      <p className="measure" style={S.intro} data-testid="quality-intro">
        Based on what you’ve entered, here’s how each section of your CV reads for {specLabel} — not just what’s present,
        but how strongly your entries land. A read of common patterns, not a score or a guarantee.
      </p>
      ) : intro}
      {/* KEPT ADJACENT to the intro AND to the summary below — the scope caveat is what the summary is scoped by. */}
      {scopeNote === undefined ? (
      <p style={S.scopeNote} data-testid="quality-scope-note">
        This read assesses section coverage and evidence framing — it does not assess specialty-specific clinical breadth or program fit.
      </p>
      ) : scopeNote}

      {readShown && (
        /* the consultant's opening conclusion — full text, normal reading size (NOT a compressed scorecard). */
        <p className="measure" style={S.overall} data-testid="quality-overall">{read.overall || read.lead}</p>
      )}

      {/* A DISTINCT action (adds the relevance read) — not a second generic re-run. */}
      {readShown && relevanceOffer && !read.specialty_relevance && isSpecialtyDepthDisplayName(specialtyName) && (
        <p style={S.srUpgradeOffer} data-testid="sr-upgrade-offer">
          <button className="btn btn-ghost" style={S.srUpgradeBtn} onClick={onRerun} disabled={rerunning} data-testid="sr-upgrade-offer-btn">
            {rerunning ? 'Re-running…' : <>Re-run to add the {specialtyName} relevance read <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></>}
          </button>
        </p>
      )}

      {/* STALE NOTICE — INFORMATIONAL only. The canonical re-run sits at the top, so this banner carries no button of
          its own (Sol: exactly one re-run affordance). */}
      {readShown && stale && (
        <div style={S.staleGate} data-testid="quality-stale">
          <span>
            This review was for an earlier version of your CV — you’ve edited since.
            {/* DETAIL COUNT — a simple cardinality. No fraction, no denominator; omitted when unknown or 0. */}
            {changedEntries != null && changedEntries > 0 && (
              <span data-testid="quality-stale-count">{' '}{changedEntries === 1 ? '1 entry' : `${changedEntries} entries`} changed since this review.</span>
            )}
          </span>
        </div>
      )}

      {failed && (
        <ErrorCard
          testId="quality-failed"
          title="We couldn’t finish the quality read"
          message="Something interrupted the read just now."
          retrySafe={failedRetrySafe}
          // Round-3 #2 — retry REPEATS THE FAILED REQUEST KIND: a failed refresh retries WITH ?refresh=1 (the server
          // honors the grace marker only there — a plain retry would paywall a recovering grace CV); a failed plain
          // load retries plain. (`read` is cleared on a failed refresh, so it can no longer stand in for intent.)
          onRetry={onRetry}
          guidance="Try again in a moment — if it keeps happening, a re-import of your CV usually resolves it."
        />
      )}

      {/* PARSE-QUALITY WARNING — a bad parse (the 2-column / PDF scramble class). NUMERICS POLICY: qualitative. */}
      {parseQuality?.poor && !proceedAnyway && (
        <div style={S.parseWarn} data-testid="quality-parse-warning">
          <h2 style={S.parseWarnTitle}>I couldn’t cleanly separate this CV into entries</h2>
          <p style={{ margin: '0 0 var(--space-3)' }}>
            Several of your experience entries came through without their content — so the read below would be
            incomplete. This usually happens with <strong>two-column layouts</strong> or text pasted from a
            <strong> PDF</strong>, where the columns get interleaved.
          </p>
          <p style={{ margin: '0 0 var(--space-4)' }}>To get a complete read, try re-importing your CV <strong>as a single column</strong>, or pasting it <strong>section by section</strong>.</p>
          <button className="btn btn-secondary" onClick={onProceedAnyway} data-testid="quality-proceed-anyway">Review the partial read anyway <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></button>
        </div>
      )}

      {/* L1 — THE SECTION CARDS. They scroll normally beneath the overview. */}
      {readShown && (
        // RE-RUN IN PROGRESS: the cards STAY on screen, visibly in progress — reduced opacity + aria-busy + an
        // INDETERMINATE strip at the top. NUMERICS POLICY: indeterminate only.
        <div
          style={rerunning ? { ...S.readBody, ...S.readBodyBusy } : S.readBody}
          aria-busy={rerunning || undefined}
          data-busy={rerunning ? 'true' : undefined}
          data-testid="quality-read-body"
        >
          {rerunning && <BusyStrip />}
          {notices}
          {/* Specialty relevance — a CHIP ROW (the ~35 per-entry template sentences are DELETED). */}
          {read.specialty_relevance && <SpecialtyRelevancePanel rel={read.specialty_relevance} cvId={cvId} specialtyName={specialtyName || 'your specialty'} onHide={onHideRelevance} />}
          {/* C5 — ONE consolidated "couldn't be read" panel. */}
          {read.unreadable && read.unreadable.length > 0 && (
            <div style={S.unreadablePanel} data-testid="quality-unreadable-panel">
              <p style={S.unreadableHeading}>Couldn’t be read</p>
              {read.unreadable.map((u, i) => (
                <p key={`${u.section}-${u.title}-${i}`} style={S.unreadableRow}>
                  {u.section} → {u.title} — no readable bullets were found. If this came from an import, re-import or check the original CV.
                </p>
              ))}
            </div>
          )}
          {/* FIX 1 (re-probe) — the read-level "Confirm before you send" panel: accuracy checks on CREDENTIAL
              sections, above the section cards deliberately (a status/venue claim is the highest-stakes thing). */}
          {read.credentialCautions && read.credentialCautions.length > 0 && (
            <ConfirmBeforeYouSend cautions={read.credentialCautions} cvId={cvId} />
          )}
          {sections.map((s, i) => (
            <SectionCard
              key={s.section}
              s={s}
              startHere={i === startAt}
              onReview={() => { onInteract(); onFocusSection(s.section); }}
              editorial={read.editorial}
              stale={stale}
              entryFactsLink={entryFactsLink}
            />
          ))}
          {/* L3 — the on-demand DETAIL layer: methodology + the vocabulary. Never in the reading path. */}
          {methodology === undefined ? <DetailLayer canmedsCap={read.canmedsCap} /> : methodology}

          <div style={{ marginTop: 'var(--space-5)' }}>
            <button className="btn btn-primary" onClick={onCraft} data-testid="quality-craft">Keep building this CV</button>
          </div>
        </div>
      )}

      {/* the full-CV drawer — persistent at L1 as well as L2 */}
      <CvFullDrawer cvId={cvId} open={cvDrawerOpen} onOpen={() => setCvDrawerOpen(true)} onClose={() => setCvDrawerOpen(false)} focusSection={null} />
    </main>
  );
}

// The inline re-run spinner. Decorative — the button's own label ("Re-running…") carries the meaning, and the polite
// live region carries the announcement, so the animation is hidden from assistive tech.
function Spinner() {
  const still = prefersReducedMotion();
  return (
    <span
      aria-hidden="true"
      data-testid="quality-rerun-spinner"
      data-static={still ? 'true' : undefined}
      style={still ? S.spinnerStill : S.spinner}
    />
  );
}

// The INDETERMINATE progress strip at the top of the read. No percentage, no fraction — it says "working", not
// "how far". Under prefers-reduced-motion it renders as a static band with a quiet "Working…" label instead.
function BusyStrip() {
  const still = prefersReducedMotion();
  return (
    <div style={S.busyStripTrack} data-testid="quality-busy-strip" data-static={still ? 'true' : undefined} aria-hidden="true">
      {still ? <span style={S.busyStill}>Working…</span> : <span style={S.busyStripBar} />}
    </div>
  );
}

// Specialty Depth v2 — client-side mirror of the server SPECIALTY_REGISTRY's display names, for the legacy-read
// upgrade offer ONLY (the panel itself renders purely off read.specialty_relevance).
const SPECIALTY_DEPTH_DISPLAY_NAMES = new Set(['family medicine', 'internal medicine', 'psychiatry', 'pediatrics']);
function isSpecialtyDepthDisplayName(specialtyName: string): boolean {
  return SPECIALTY_DEPTH_DISPLAY_NAMES.has(specialtyName.trim().toLowerCase());
}

function joinPlain(xs: string[]): string {
  const ys = xs.filter(Boolean);
  if (ys.length === 0) return '';
  if (ys.length === 1) return ys[0];
  if (ys.length === 2) return `${ys[0]} and ${ys[1]}`;
  return `${ys.slice(0, -1).join(', ')}, and ${ys[ys.length - 1]}`;
}

// L3 — the DETAIL layer. Methodology + vocabulary, on demand, never in the reading path. It is USER-FACING, so the
// numerics policy applies here too: nothing in here renders a fraction.
function DetailLayer({ canmedsCap }: { canmedsCap?: string }) {
  return (
    <div style={S.detailLayer}>
      <details style={S.detail} data-testid="quality-l3-methodology">
        <summary style={S.detailSummary}>What this review looks for</summary>
        <p style={S.detailBody}>
          This review looks for whether your contribution is clear, whether claimed outcomes are anchored in something
          observable, and whether named roles are demonstrated through action. Clearing these checks means nothing was
          flagged — it is not proof that a bullet is strong. Coverage is assessed separately using national training
          and competency frameworks, not program selection criteria.
        </p>
        {canmedsCap && <p style={S.detailBody} data-testid="quality-canmeds-cap">{canmedsCap}</p>}
      </details>
      <details style={S.detail} data-testid="quality-l3-definitions">
        <summary style={S.detailSummary}>What the terms mean</summary>
        <p style={S.detailBody}><strong>Impact framing</strong> — whether a bullet shows what changed because you were there, rather than what you were responsible for.</p>
        {/* FIX 1 — "integrity flag" language is gone from every user-facing surface: these are automated ACCURACY
            AND WORDING checks, shown with their own question so they can actually be acted on. */}
        <p style={S.detailBody}><strong>Accuracy &amp; wording checks</strong> — a separate, automated check for wording that may outrun the evidence on the page (a status or venue that may not be exact, a blurred line between the team's work and yours). They are checks to confirm, not findings: they never change the quality verdict, and each one is shown beside its own bullet with the question to answer.</p>
        <p style={S.detailBody}><strong>Evidence shown</strong> — the kinds of concrete detail found in your own words: {joinPlain(Object.values(MARKER_PHRASE_CLIENT))}.</p>
        <p style={S.detailBody}><strong>Specialty coverage</strong> — whether entries in a section were matched to an area the specialty's training framework describes. "Not applicable" means nothing was matched, not that something is missing.</p>
      </details>
    </div>
  );
}

// Label an entry for display. OWNER RULING (amendment, 2026-08-04): "not just when there is colision. it should be
// default." The organization is part of WHAT THE ROLE IS — "Family Medicine Observer" is not the same role at two
// different clinics — so it renders WHENEVER the entry has one, collision or not. Two omission rules survive: a blank
// org adds nothing, and an org that IS the title would print "X — X" (an untitled entry's title already fell back to
// its org upstream).
// ORDINALS are now the LAST RESORT ONLY: entries whose FINAL labels still collide — missing orgs, or two identical
// orgs. That is also the path every read persisted BEFORE the org field takes (no organization key anywhere), so
// those reads keep exactly today's rendering; stored reads only refresh on a re-run.
function labelOf(t: { title: string; organization?: string }): string {
  const org = (t.organization ?? '').trim();
  const title = t.title;
  return org.length > 0 && org !== title.trim() ? `${title} — ${org}` : title;
}
function disambiguate(entryTitles: { id: string; title: string; organization?: string }[]): Map<string, string> {
  const byLabel = new Map<string, { id: string; label: string }[]>();
  for (const t of entryTitles) {
    const label = labelOf(t);
    byLabel.set(label, [...(byLabel.get(label) ?? []), { id: t.id, label }]);
  }
  const out = new Map<string, string>();
  for (const [label, group] of byLabel) {
    if (group.length < 2) out.set(group[0].id, label);
    else group.forEach((t, i) => out.set(t.id, `${label} (${i + 1})`));
  }
  return out;
}

// The display label for every entry a SECTION prints — its review items, its strong entries, and its editorial
// notes, resolved together so one entry reads identically everywhere it appears in that section (and so the ordinal
// last resort counts within the section the reader is actually looking at). Keyed by entry_id.
function sectionEntryLabels(s: SectionQuality, editorial?: QEditorial[]): Map<string, string> {
  const byId = new Map<string, { id: string; title: string; organization?: string }>();
  const add = (e: { entry_id: string; entry_title: string; entry_organization?: string }) => {
    if (!byId.has(e.entry_id)) byId.set(e.entry_id, { id: e.entry_id, title: e.entry_title ?? '', organization: e.entry_organization });
  };
  for (const it of s.items ?? []) add(it);
  for (const e of s.strongEntries ?? []) add(e);
  for (const e of (editorial ?? []).filter((x) => x.section === s.section)) add(e);
  return disambiguate([...byId.values()]);
}

// Specialty Depth v2 — the "[Specialty] relevance" CHIP ROW.
// REDESIGN (mechanical fix 1): the per-entry template sentences ("Your X makes Y visible for Z.") are DELETED — they
// repeated the chip label ~35 times and carried no information. The panel is now a chip row: one chip per detected
// signal with a simple entry CARDINALITY ("· 4 entries" — a count, never a fraction), one chip open at a time,
// expanding to the matched entries as disambiguated anchors, each still individually correctable (hide).
// EVERY string is a FIXED template parameterized only by the specialty display name, or ratified content riding in
// `rel`; entry TITLES are the user's own.
function SpecialtyRelevancePanel({ rel, cvId, specialtyName, onHide }: { rel: SpecialtyRelevance; cvId: string; specialtyName: string; onHide?: HideRelevance }) {
  const [signals, setSignals] = useState(rel.signals);
  const [openChip, setOpenChip] = useState<string | null>(null); // ONE chip open at a time
  void cvId;

  async function hide(signalId: string, entryId: string) {
    if (!onHide) return;
    const r = await onHide(signalId, entryId);
    if (!r || !r.ok) return; // firewall/quiet-fail: a correction that didn't save leaves the link exactly as shown before
    setSignals((prev) => prev.map((s) => (
      s.id === signalId
        ? { ...s, entry_ids: s.entry_ids.filter((id) => id !== entryId), hidden_entry_ids: [...s.hidden_entry_ids, entryId], shown: r.shown ?? s.entry_ids.length > 1 }
        : s
    )));
  }

  const shownSignals = signals.filter((s) => s.shown);
  const otherSignals = signals.filter((s) => !s.shown);
  const open = shownSignals.find((s) => s.id === openChip) ?? null;
  // Collision scope is the OPEN CHIP'S OWN anchor list — the reader only ever compares the anchors sitting side by
  // side under one chip, so a title that repeats across two different chips needs no disambiguation.
  const openEntryIds = open ? open.entry_ids.join('|') : '';
  const labels = useMemo(
    () => disambiguate(rel.entry_titles.filter((t) => openEntryIds.split('|').includes(t.id))),
    [rel.entry_titles, openEntryIds],
  );
  const titleOf = (id: string): string => labels.get(id) ?? '';

  return (
    <div style={S.srPanel} data-testid={`sr-panel-${rel.specialty}`}>
      <h2 style={S.srHeading}>{specialtyName} relevance</h2>
      <p style={S.srSubline}>
        {specialtyName}-relevant experience visible in your CV — drawn from national training and competency
        frameworks, not program selection criteria.
      </p>
      {shownSignals.length > 0 && (
        <div style={S.srChips} data-testid="sr-chips">
          {shownSignals.map((s) => {
            const n = s.entry_ids.length;
            const isOpen = openChip === s.id;
            return (
              <button
                key={s.id}
                type="button"
                style={{ ...S.srChip, ...(isOpen ? S.srChipOn : {}) }}
                aria-expanded={isOpen}
                data-testid={`sr-chip-${s.id}`}
                onClick={() => setOpenChip(isOpen ? null : s.id)}
              >
                <IconCheck aria-hidden="true" /> {s.plain_language} · {n} {n === 1 ? 'entry' : 'entries'}
              </button>
            );
          })}
        </div>
      )}
      {open && (
        <div style={S.srSignal} data-testid={`sr-signal-${open.id}`}>
          <p style={S.srSignalLine}><IconCheck aria-hidden="true" /> {open.plain_language} — shown in {joinPlain(open.entry_ids.map((id) => titleOf(id)).filter(Boolean))}</p>
          <p style={S.srWhatCounts}>{open.what_counts}</p>
          {open.entry_ids.map((id) => {
            const t = titleOf(id);
            if (!t) return null;
            return (
              <p key={id} style={S.srAdjacent}>
                <span>{t}</span>
                {onHide && (
                <button
                  type="button"
                  style={S.srHideBtn}
                  title="This link isn't accurate — hide it"
                  data-testid={`sr-hide-${open.id}-${id}`}
                  aria-label="Hide this link"
                  onClick={() => void hide(open.id, id)}
                >
                  <IconClose aria-hidden="true" />
                </button>
                )}
              </p>
            );
          })}
          <p style={S.srOtherBasis}>(Basis: {open.provenance.source})</p>
        </div>
      )}
      {otherSignals.length > 0 && (
        <details style={S.srOther} data-testid="sr-other-areas">
          <summary style={S.srOtherSummary}>Other {specialtyName}-relevant areas not visible here — not a checklist or a requirement</summary>
          <p style={S.srOtherNote}>No CV shows everything.</p>
          {otherSignals.map((s) => (
            <div key={s.id} style={S.srOtherItem}>
              <p style={S.srOtherPlain}>{s.plain_language}</p>
              <p style={S.srOtherRecover}>{s.recover_question}</p>
              <p style={S.srOtherBasis}>(Basis: {s.provenance.source})</p>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

// "Reviewed just now / today / on <date>" — a light freshness caption (the user's own assessment timestamp).
/**
 * "Reviewed {when}". 2026-08-23 — THE VAGUE MIDDLE RUNG IS GONE.
 *
 * It used to say "today" for anything under 24 hours, and a review the applicant's own dashboard dates to a
 * different day read as "Reviewed today" beside it. Whether that came from a re-computation on load or from a
 * timestamp the applicant does not think of as the review's date, the SENTENCE was the problem: "today" is a
 * freshness claim with no way for a reader to check it, and it was the only rung that could contradict a date
 * shown elsewhere. Anything an hour old or more now states its own DATE, which no other surface can contradict.
 * The two short rungs stay: they are about a re-run the applicant just watched happen.
 */
function fmtWhen(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  return new Date(ms).toLocaleDateString();
}

// FIX 5 — "Do this first" TEMPLATE FATIGUE, fixed at the DISPLAY layer (the server payload is untouched).
// The server's covered-and-weak frame opens every such card with "You have the <section> entries — the work now
// is the framing: " — a verbose restatement of the section name that carried no information and rendered on 4 of 4
// cards. We drop that preamble so the line LEADS with the section-specific move, and where the server's trailing
// move is its GENERIC fallback we point at the section's first flagged entry BY NAME instead.
// HONESTY: this invents nothing and hides nothing. Where two sections genuinely share a diagnosis, their lines
// still read alike — the fix removes restatement, it does NOT fake variety. Numerics policy: no counts added.
const REC_PREAMBLE = /^You have the .+? entries\s*—\s*the work now is the framing:\s*/;
const REC_GENERIC_MOVE = 'Start with the entry you know best and name your specific contribution in it.';

// cc-11 ITEM 6 (second half) — A NON-CURRENT READ MAY NOT PRESENT AN ASSERTIVE SPECIALTY ENDORSEMENT AS CURRENT.
//
// "This section is solid for Family Medicine." is a verdict about CONTENT. When the stored read is stale the content
// it was computed from no longer exists, so rendering that sentence plainly asserts, of the CV on screen, something
// the engine has not assessed. Every other stale surface is informational; this one is a claim, and it is the only
// piece of section copy that names the specialty approvingly.
//
// SUPPRESS OR MARK — we MARK, deliberately. Suppressing would silently remove the only affirming line in the read
// and leave the user unable to tell whether the section had been assessed at all. The marker is appended, never
// woven in, so the server's sentence is still rendered verbatim and the qualification cannot be missed.
const STALE_ENDORSEMENT_RE = /\bsolid\b/i;
export const STALE_ENDORSEMENT_MARK = 'This was assessed before your latest edits — re-run the review to confirm it still holds.';
export function markStaleEndorsement(rec: string, stale?: boolean): string {
  const out = (rec ?? '').trim();
  if (!stale || !STALE_ENDORSEMENT_RE.test(out)) return out;
  return `${out} ${STALE_ENDORSEMENT_MARK}`;
}
export function tightenRecommendation(rec: string, firstFlaggedTitle?: string): string {
  let out = (rec ?? '').trim();
  if (REC_PREAMBLE.test(out)) {
    out = out.replace(REC_PREAMBLE, '');
    out = out.charAt(0).toUpperCase() + out.slice(1); // the remainder now opens the sentence
  }
  // Only the GENERIC fallback is replaced — the per-section FIRST_MOVE coaching is real, section-specific
  // guidance and is left exactly as the server wrote it.
  const title = firstFlaggedTitle?.trim();
  if (title && out.includes(REC_GENERIC_MOVE)) {
    out = out.replace(REC_GENERIC_MOVE, `First flagged entry in this section: “${title}” — name your specific contribution in it.`);
  } else if (title && !/\bStart with\b/.test(out) && !out.includes('First flagged entry')) {
    // A stored read whose recommendation carries NO trailing move at all (observed on a live CV: four cards that
    // stopped after the gate phrase). Point at the section's own first flagged entry so the line says where to
    // begin. The title is the USER'S own text; the label states the mechanical selection (document order)
    // rather than implying the engine's considered priority (Sol wording ruling, 2026-08-04).
    out = `${out.replace(/\s+$/, '')} First flagged entry in this section: “${title}”.`;
  }
  return out;
}
// The entry a "start with…" pointer should name: the first FLAGGED entry in the section, in document order. It uses
// the SAME org-qualified label the heading shows, so the pointer names one unambiguous entry ("Family Medicine
// Observer — Northgate Clinic") rather than a title the section prints twice.
function firstFlaggedTitleOf(items: QItem[], labels?: Map<string, string>): string | undefined {
  const it = items.find((x) => (x.bullets?.length ?? 0) > 0 && x.entry_title?.trim());
  if (!it) return undefined;
  return labels?.get(it.entry_id) ?? it.entry_title;
}

// L1 — a COMPACT section card: plain-language verdict + ONE concrete next action + the way in to the section review.
// The framework vocabulary (CanMEDS role frame, the labelled two-axis verdict, the standard) is behind "details".
function SectionCard({ s, startHere, onReview, editorial, stale, entryFactsLink }: { s: SectionQuality; startHere: boolean; onReview: () => void; editorial?: QEditorial[]; stale?: boolean; entryFactsLink?: EntryFactsLink }) {
  const items = s.items ?? [];                                       // DEFENSIVE: a malformed response degrades, never crashes
  const labels = sectionEntryLabels(s, editorial);                   // org-qualified headings, one label per entry_id
  const [open, setOpen] = useState(false);                           // details are OPT-IN at L1 — the wall is what we removed
  const expandable = items.length > 0 || !!s.standard || !!s.assessment || (s.strongEntries?.length ?? 0) > 0;
  const tid = s.section.replace(/\W+/g, '-').toLowerCase();
  const cov = coverageWord(s.assessment);
  const rating = s.bulletsRating ?? s.rating;
  // FIX 1 — the accuracy checks are work inside the section too, so they count toward the way in. Without this a
  // section whose ONLY finding is a caution would disclose a count on the card with no route to see it — the
  // hidden-but-counted state again. Still a simple CARDINALITY, never a fraction.
  const issueCount = items.reduce((n, it) => n + (it.bullets?.length ?? 0), 0) + (s.integrityCautions?.length ?? 0);
  return (
    <div className="card-interactive" style={{ ...S.card, ...(startHere ? S.cardStart : {}) }} data-testid={`quality-section-${tid}`}>
      <button style={S.cardHead} onClick={() => expandable && setOpen((o) => !o)} data-testid={`quality-toggle-${tid}`} aria-expanded={open}>
        <span style={S.secName}>{s.section}</span>
        <span style={S.headRight}>
          {startHere && <span style={S.startHere} data-testid="quality-start-here">Start here</span>}
          {expandable && <span style={S.chev} data-testid="quality-chevron">{open ? '▾' : '▸ details'}</span>}
        </span>
      </button>
      <div style={S.body}>
        {/* PLAIN-LANGUAGE verdict — the L1 headline. No framework vocabulary. */}
        {/* FIX 3 — below three bullets the headline is the QUALITATIVE state, never the categorical verdict. The
            old "read this verdict cautiously" footnote did not undo a categorical headline; this removes the
            headline instead. It renders in neutral ink — a category's colour is a category. */}
        {s.total > 0 && (s.smallSectionState
          ? <p style={{ ...S.plainVerdict, color: 'var(--ink)' }} data-testid={`quality-small-state-${tid}`}>{s.smallSectionState}</p>
          : <p style={{ ...S.plainVerdict, color: ratingInk(rating) }} data-testid={`quality-plain-verdict-${tid}`}>{PLAIN_VERDICT[rating]}</p>
        )}
        {/* the qualitative evidence line + the LOUD integrity disclosure (an actionable obligation, count kept). */}
        {s.evidence && <EvidenceLine e={s.evidence} tid={tid} counts={cautionCounts(s)} />}
        {/* ONE concrete next action. */}
        {s.recommendation && <p style={S.sectionRec} data-testid="quality-recommendation"><strong>Do this first: </strong>{markStaleEndorsement(tightenRecommendation(s.recommendation, firstFlaggedTitleOf(items, labels)), stale)}</p>}
        {/* C5 — the deterministic entry-length editorial check: a calm note, never a rating input. */}
        {(editorial ?? []).filter((e) => e.section === s.section).map((e) => (
          <p key={e.entry_id} style={S.editorial} data-testid={`quality-editorial-${e.entry_id}`}>
            {e.entry_title?.trim() ? (labels.get(e.entry_id) ?? e.entry_title) : 'One entry here'} has {e.points} bullet points — usually too many for CaRMS; which 3–5 best distinguish this role?
          </p>
        ))}
        {/* THE PRIMARY AFFORDANCE — filled, full-width, and it says how much work is inside. The count is a simple
            CARDINALITY ("· 6 items"), never a fraction: the numerics policy holds. */}
        {issueCount > 0 && (
          <button className="btn btn-primary" style={S.reviewBtn} onClick={onReview} data-testid={`quality-open-${tid}`}>
            Review this section · {issueCount} {issueCount === 1 ? 'item' : 'items'} <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} />
          </button>
        )}
        {/* FIX 2 — the display cap, disclosed. Sits directly under the way-in so the count on the button is never
            read as the whole of the work: the button counts what is SHOWN, this names what is not. */}
        <CapDisclosure n={s.suppressedItems ?? 0} tid={tid} />
        {open && (
          <>
            {/* L3-in-place: the framework detail for this section, on demand. */}
            {s.assessment?.roleFrame && <p style={S.roleFrame} data-testid="quality-role-frame">{s.assessment.roleFrame}</p>}
            {/* FIX 3 — the detail expander must not smuggle the category back in: for a 1–2 bullet section the
                "Impact framing: <word>" axis is DROPPED (coverage, which is a count of confirmed tags rather than a
                density reading, still renders). */}
            {s.total > 0 && (
              <p style={S.verdict} data-testid="quality-verdict">
                {cov && <>Specialty coverage: <strong>{cov}</strong>{s.smallSectionState ? '' : ' · '}</>}
                {!s.smallSectionState && <>Impact framing: <span style={{ color: ratingInk(rating), fontWeight: 600 }}>{QUALITY_WORD[rating]}</span></>}
                {s.smallSectionState && <span style={{ color: 'var(--ink-subtle)' }}> — not enough material here for an overall impact-framing rating.</span>}
              </p>
            )}
            {/* mechanical fix 3 — "not applicable" always rides with one plain sentence of WHY. */}
            {cov === 'not applicable' && (
              <p style={S.covWhy} data-testid={`quality-coverage-why-${tid}`}>
                Nothing in this section was matched to an area {s.assessment?.canmeds ? 'this specialty’s training framework describes' : 'the specialty’s training framework describes'} — so there is nothing here to compare against. That is not a missing requirement.
              </p>
            )}
            {s.assessment && <p style={S.assessDetail} data-testid="quality-assessment">{s.assessment.line}</p>}
            {s.standard && (
              <div style={S.standard} data-testid="quality-standard">
                <p style={S.standardLabel}>What a strong {s.section} entry shows</p>
                <p style={S.standardBody}>{s.standard.exemplar}</p>
              </div>
            )}
            {/* STRONG section: SHOW the entries, affirmingly — read-only, no critique. */}
            {s.strongEntries && s.strongEntries.length > 0 && (
              <div data-testid="quality-strong-entries">
                {/* COPY CONTRACT (Sol-ratified 2026-08-07) — "your work here lands" was a QUALITY claim generated by
                    flaggedBullets === 0, the exact absence-of-flags -> sufficiency inference the contract forbids. */}
                {(s.evidence?.flaggedBullets ?? 0) === 0 && <p style={S.strongLead}>Nothing flagged in these entries:</p>}
                {s.strongEntries.map((e, i) => (
                  <div key={e.entry_id} style={S.role}>
                    {e.entry_title && <p style={S.roleTitle}>{labels.get(e.entry_id) ?? e.entry_title}</p>}
                    {/* COPY CONTRACT (owner-endorsed, Sol-ratified, 2026-08-07) — see the block at the head of
                        GATE_FALLBACKS in workers/src/lib/classifierPrompt.ts. This label used to open with the word
                        Strong. Its generating predicate is: ONE OR MORE markers from a HETEROGENEOUS set (a metric,
                        a named method, a stated scope, a named setting) plus no flags on the entry. Sol: "'Strong'
                        is not earned by one or more heterogeneous markers plus no flags. A metric may be
                        meaningful; merely naming the setting is not enough to establish overall entry strength."
                        "Evidence shown" claims exactly what the marker list demonstrates — no more. */}
                    {e.markers && e.markers.length > 0 && (
                      <p style={S.strongMarkers} data-testid={`quality-strong-markers-${e.entry_id}`}>
                        Evidence shown — {joinMarkers(e.markers)}
                      </p>
                    )}
                    {e.bullets.map((b) => <BulletText key={b.bullet_id} text={b.bullet_text} />)}
                    <EntryFactsLinkLine link={entryFactsLink} entryId={e.entry_id} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// C3b — join an entry's earned markers into "a metric and the method" for the "Evidence shown — …" line.
function joinMarkers(markers: string[]): string {
  const phrases = markers.map((m) => MARKER_PHRASE_CLIENT[m] ?? m);
  if (phrases.length <= 1) return phrases[0] ?? '';
  if (phrases.length === 2) return `${phrases[0]} and ${phrases[1]}`;
  return `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`;
}

// The disclosed evidence, REDESIGNED to the numerics policy: WHAT was flagged (plain-language gate phrases) and what
// evidence was shown, with NO ratio and NO per-gate tally. The small-sample notice is QUALITATIVE. The integrity
// disclosure keeps its COUNT — it is an actionable obligation ("please confirm"), the explicit policy exception —
// and is rendered LOUD, on its own line, never folded into the quiet supporting text.
function EvidenceLine({ e, tid, counts }: { e: QEvidence; tid: string; counts: { verify: number; confirm: number } }) {
  const gateParts = Object.entries(e.byGate).filter(([, n]) => n > 0).map(([gate]) => GATE_PHRASE_CLIENT[gate] ?? gate);
  const markerParts = Object.entries(e.evidenceMarkers ?? {}).filter(([, n]) => n > 0).map(([tok]) => MARKER_PHRASE_CLIENT[tok] ?? tok);
  return (
    <>
      <p style={S.evidence} data-testid={`quality-evidence-${tid}`}>
        {gateParts.length > 0 ? `What was flagged: ${joinPlain(gateParts)}.` : 'Nothing was flagged in this section.'}
        {markerParts.length > 0 ? ` Evidence shown: ${joinPlain(markerParts)}.` : ''}
        {e.limitedSample ? ' Limited evidence here — read this verdict cautiously.' : ''}
      </p>
      {/* FIX 1 — the disclosure is now split by BAND and speaks in accuracy/wording language ("integrity flag" is
          gone from every user-facing surface). The COUNT stays — it is the actionable obligation, and it is a simple
          cardinality, never a fraction. The checks themselves are shown, with their diagnosis + question, in L2. */}
      {counts.verify > 0 && (
        <p style={S.integrity} data-testid={`quality-integrity-${tid}`}>
          Accuracy check: {counts.verify} {counts.verify === 1 ? 'item' : 'items'} to confirm before you send this CV — a stated status or venue may not be exact. Automated check, not a finding.
        </p>
      )}
      {counts.confirm > 0 && (
        <p style={S.wordingCheck} data-testid={`quality-wording-check-${tid}`}>
          Before sending: {counts.confirm} wording {counts.confirm === 1 ? 'check' : 'checks'} to confirm. Automated check, not a finding.
        </p>
      )}
    </>
  );
}

// FIX 1 — ONE automated accuracy caution, rendered BESIDE its own bullet. Sol's ratified framing, kept intact in every
// variant: the HEDGE ("automated check, not a finding"), the hedged diagnosis, the question, and a DISMISS affordance.
// It is a CHECK TO CONFIRM, never an adjudicated conclusion, and it never touches the section's rating.
// `onDismiss` is OPTIONAL and additive — the row-aligned L2 callers pass nothing and are unchanged. The credential
// panel passes it so a group/section heading whose last check was dismissed disappears with it, instead of leaving an
// empty heading behind.
// ── cc-10 ITEM 6 — THE EDITORIAL DISPOSITION LABELS (deterministic, derived from the gate id; no model output) ─────
//
// SCOPE, RULED BY SOL: exactly TWO labels ship this round.
//   · an adequacy (impact) finding      → "Sharpen"
//   · the claim-risk integrity gate     → "Potential claim risk"
// The third label the brief originally carried ("No issue flagged in this read") is DELIBERATELY NOT BUILT: a bullet
// that passes has no per-point card to render it on (`QualityItem.bullets` carries flagged bullets only), and the
// frozen left-hand point list already encodes that state visually — a point rendered in ordinary ink is a point with
// nothing flagged. Adding the payload or touching RolePointList to say it in words was rejected as not worth the
// regression risk on a frozen surface. "Combine" and "Consider cutting" remain deferred and are not built.
//
// THE COPY CONTRACT BINDS THESE TWO STRINGS. "Sharpen" is an INSTRUCTION derived from a failure detector that fired —
// it claims only that something was flagged, never that the bullet is weak overall. "Potential claim risk" carries
// the same hedge as the caution it labels. Neither may drift toward "already sufficient", "strong", "keep", or
// "ready": absence of a failure supports only absence-of-flag, and no label here is a positive rating.
// A bullet carrying BOTH an adequacy finding and claim risk correctly shows BOTH labels — they are different
// observations about the same line, which is exactly what gs-4 exists to make possible.
const CLAIM_RISK_GATE = 'UNSUPPORTED_OUTCOME_CLAIM';
// R5.7b fix — the label is the REGISTERED neutral verb (`CS-LEGACY-ROW-ACTION`, "Clarify"): the new engine's rows
// are clarification prompts and questions (§8.3 F2), and "Sharpen" asserted a failure. Same slot, same chip.
const DISPOSITION_SHARPEN = COPY['CS-LEGACY-ROW-ACTION'];
const DISPOSITION_CLAIM_RISK = 'Potential claim risk';

/** The quiet on-theme chip the labels render in — the same idiom as the "Point N" chip it sits beside, one shade
 *  further back so the label never competes with the diagnosis it introduces. */
function DispositionLabel({ text, testid }: { text: string; testid: string }) {
  return <span style={S.dispositionLabel} data-testid={testid}>{text}</span>;
}

function IntegrityCaution({ c, cvId, onDismiss }: { c: QIntegrityCaution; cvId: string; onDismiss?: () => void }) {
  const [dismissed, setDismissed] = useState(() => readDismissed(cvId, c));
  if (dismissed) return null;
  const verify = c.urgency === 'verify';
  // An ENTRY-target check is about the entry's own HEADER, not a bullet. The hedge is identical in kind — an automated
  // check to confirm, never an adjudicated finding — but it must name the right thing, or the user looks at the bullets
  // underneath for a problem that is in the title.
  const entryTarget = isEntryTarget(c);
  const head = entryTarget
    ? (c.target === 'entry-organization'
      ? 'Automated accuracy check — not a finding: this entry’s organization may state a venue that is not exact.'
      : 'Automated accuracy check — not a finding: this entry’s title may state a status or venue that is not exact.')
    : (verify
      ? 'Automated accuracy check — not a finding: this wording may state a status or venue that is not exact.'
      // cc-10 DEFECT FOUND BY THE BROWSER CHECK, not by any unit test. The calm band's headline was written when
      // every 'confirm' gate was about CONTRIBUTION, so adding the claim-risk gate to that band silently gave it the
      // headline "this wording may make your individual contribution unclear" — copy that names the WRONG CONCERN.
      // A user reading it would look for a contribution problem in a line whose actual risk is an unsupported
      // outcome claim. The headline is now gate-aware; it keeps the same hedge and the same "not a finding" framing.
      : c.gate_id === CLAIM_RISK_GATE
        ? 'Automated accuracy check — not a finding: this wording may imply a result the line does not show was observed or measured.'
        : 'Automated accuracy check — not a finding: this wording may make your individual contribution unclear.');
  return (
    <div
      style={verify ? { ...S.caution, ...S.cautionVerify } : S.caution}
      data-testid={`quality-caution-${cautionKey(c)}`}
      data-urgency={c.urgency}
      data-target={c.target ?? 'bullet'}
    >
      {c.gate_id === CLAIM_RISK_GATE && (
        <DispositionLabel text={DISPOSITION_CLAIM_RISK} testid={`quality-disposition-claim-risk-${cautionKey(c)}`} />
      )}
      <p style={S.cautionHead}>{head}</p>
      <p style={S.cautionDiag}>{c.diagnosis}</p>
      <p style={S.cautionQ}>Before sending, confirm: {c.question}</p>
      {/* cc-7 (Sol round-4): the same deterministic escape-route hint under a quantity-classified caution question. */}
      {isQuantityAsk(c.question) && (
        <p style={S.quantityHint} data-testid="quantity-hint">{QUANTITY_HINT_COPY}</p>
      )}
      <button
        type="button"
        className="btn btn-ghost"
        style={S.cautionDismiss}
        data-testid={`quality-caution-dismiss-${cautionKey(c)}`}
        onClick={() => { writeDismissed(cvId, c); setDismissed(true); onDismiss?.(); }}
      >
        If the wording is already exact, dismiss this check
      </button>
    </div>
  );
}

// FIX 1 (re-probe) — THE "CONFIRM BEFORE YOU SEND" PANEL. The accuracy checks on CREDENTIAL sections (Publications,
// Awards & Honours, Certifications, …), which are PRESENCE-ONLY for quality and so have no section card and no L2 row
// to hang a check beside. Before this they were computed by the classifier and shown NOWHERE.
//
// WHAT THIS PANEL DELIBERATELY DOES NOT CONTAIN (the presence-only rule, kept intact): no rating, no verdict word, no
// density or evidence language, no "Start here" ranking, and no synthetic credential section card. It speaks ONLY on
// the ACCURACY axis, which is separate from and non-compensatory with the quality ratings — nothing here can move a
// section's verdict, because these sections have no verdict to move.
//
// ORDER: the server hands the cautions back `verify` band first, then `confirm`; this splits them into two visually
// distinct groups on that field — verify prominent (a status/venue claim a program can actually check), confirm calm
// (a wording reading). Within a band, grouping is by SECTION then by ENTRY, in the CV's own order.
//
// NUMERICS POLICY: no count, no fraction, no denominator anywhere in this panel — the checks themselves are the
// disclosure, and they are all shown.
function ConfirmBeforeYouSend({ cautions, cvId }: { cautions: QCredentialCaution[]; cvId: string }) {
  // Checks already dismissed on a previous visit are filtered out up front, so a fully dismissed panel does not render
  // an empty shell. Live dismissals are tracked here too (via IntegrityCaution's onDismiss) for the same reason.
  const [gone, setGone] = useState<string[]>([]);
  const keyOf = (c: QCredentialCaution) => cautionKey(c);
  const live = cautions.filter((c) => !gone.includes(keyOf(c)) && !readDismissed(cvId, c));
  if (live.length === 0) return null;
  const verify = live.filter((c) => c.urgency === 'verify');
  const confirm = live.filter((c) => c.urgency !== 'verify');
  const drop = (c: QCredentialCaution) => setGone((g) => [...g, keyOf(c)]);
  return (
    <div style={S.confirmPanel} data-testid="quality-confirm-panel">
      <p style={S.confirmHeading}>Confirm before you send</p>
      <p style={S.confirmNote}>
        These are automated accuracy checks on how your credentials are worded — not findings, and they do not change
        any section&rsquo;s rating. Each one is a question to answer against the original record.
      </p>
      {verify.length > 0 && (
        <ConfirmBand
          testId="quality-confirm-verify"
          heading="Check these against the original record"
          note="A stated status or venue may not be exact. These are the ones a program can check."
          style={S.confirmBandVerify}
          cautions={verify} cvId={cvId} onDismiss={drop}
        />
      )}
      {confirm.length > 0 && (
        <ConfirmBand
          testId="quality-confirm-confirm"
          heading="Wording to confirm"
          note="How your individual contribution reads. If the wording is already exact, dismiss the check."
          style={S.confirmBandCalm}
          cautions={confirm} cvId={cvId} onDismiss={drop}
        />
      )}
    </div>
  );
}

// ONE urgency band inside the panel, grouped by SECTION then by ENTRY. Each check renders under the EXACT credential
// line it is about, so the user never has to guess which publication or award is being questioned.
function ConfirmBand({ testId, heading, note, style, cautions, cvId, onDismiss }: {
  testId: string; heading: string; note: string; style: CSSProperties;
  cautions: QCredentialCaution[]; cvId: string; onDismiss: (c: QCredentialCaution) => void;
}) {
  // Two levels of insertion-ordered grouping — Map preserves the CV's own document order at both levels.
  const bySection = new Map<string, Map<string, QCredentialCaution[]>>();
  for (const c of cautions) {
    const byEntry = bySection.get(c.section) ?? bySection.set(c.section, new Map()).get(c.section)!;
    byEntry.set(c.entry_id, [...(byEntry.get(c.entry_id) ?? []), c]);
  }
  return (
    <div style={style} data-testid={testId}>
      <p style={S.confirmBandHeading}>{heading}</p>
      <p style={S.confirmBandNote}>{note}</p>
      {[...bySection].map(([section, byEntry]) => (
        <div key={section} data-testid={`quality-confirm-section-${section}`}>
          <p style={S.confirmSectionLabel}>{section}</p>
          {[...byEntry].map(([entryId, cs]) => {
            const first = cs[0];
            const label = first.entry_organization ? `${first.entry_title} — ${first.entry_organization}` : first.entry_title;
            return (
              <div key={entryId} style={S.confirmEntry}>
                {label && <p style={S.confirmEntryTitle}>{label}</p>}
                {cs.map((c) => (
                  <div key={cautionKey(c)} data-testid={`quality-confirm-item-${cautionKey(c)}`}>
                    {/* The user's OWN text, verbatim and read-only — the check sits directly under the thing it is
                        about. For an ENTRY target that text IS the entry's title/organization, which the group
                        heading above already prints; repeating it would render the same line twice, so it is
                        suppressed here and the heading serves as the claim. */}
                    {!isEntryTarget(c) && <div style={S.confirmLine}><BulletText text={c.bullet_text} /></div>}
                    <IntegrityCaution c={c} cvId={cvId} onDismiss={() => onDismiss(c)} />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// L2 — THE SECTION REVIEW, as a ROW-ALIGNED TWO-COLUMN GRID. Each FLAGGED bullet is ONE ROW: the user's own bullet
// in the left cell, that bullet's diagnosis + question + the improve affordance in the right cell, sharing a row top
// edge (an explicit `gridRow` per pair — genuine alignment, not two independent columns that drift). EVERY row is
// visible at once: there is no toggling and no one-open-at-a-time, so the correspondence is persistent and there is
// nothing left for the reader to track. Unflagged (strong) bullets render LEFT-CELL-ONLY at reduced emphasis — no
// empty right boxes. Next/Previous survive only as QUIET scroll shortcuts. Drafts and saved revisions live here, at
// the section level, so nothing the user typed is ever discarded.
// L2 point numbering (owner regression, 2026-08-08) — ONE display list per flagged role: every point the server's
// serve-time enrichment carried (`all_bullets` — clean siblings included, numbered by TRUE position), each joined to
// its flagged finding when it has one. Falls back to the flagged-only compressed list (numbered by rendered order)
// when the payload has no enrichment (a pre-fix client cache, or the enrichment degraded server-side). A flagged
// bullet deleted from the live CV since the read still renders its critique row, un-numbered, at the end.
type DisplayBullet = { bullet_id: string; bullet_text: string; num?: number; weak?: QWeakBullet };
function displayBulletsOf(it: QItem): DisplayBullet[] {
  const weakById = new Map((it.bullets ?? []).map((b) => [b.bullet_id, b] as const));
  const all = it.all_bullets ?? [];
  if (all.length === 0) return (it.bullets ?? []).map((b, i) => ({ bullet_id: b.bullet_id, bullet_text: b.bullet_text, num: i + 1, weak: b }));
  const rows: DisplayBullet[] = all.map((ab) => {
    const weak = weakById.get(ab.bullet_id);
    // a flagged row keeps the READ's frozen text (its diagnosis was written against it); clean siblings show live text
    return { bullet_id: ab.bullet_id, bullet_text: weak ? weak.bullet_text : ab.bullet_text, num: ab.position, weak };
  });
  for (const b of it.bullets ?? []) if (!all.some((ab) => ab.bullet_id === b.bullet_id)) rows.push({ bullet_id: b.bullet_id, bullet_text: b.bullet_text, weak: b });
  return rows;
}

// OWNER RULING (2026-08-08, verbatim): "the way it was before was the points numbered 1,2,3... no space between
// points. it was more or less more compact. it was simple to look at." — a role's points render as ONE compact
// numbered list, the pre-split BulletText <ol> look LITERALLY (same S.bulletList/S.bulletLi styles, native markers,
// no per-point cells/borders/gaps). Red flagged lines (S.bulletLiWeak — the old weak-line colour), black clean
// lines, `value` = the TRUE absolute position (so a clean/flagged mix numbers 1,2,3,4 even though only flagged
// points carry cards). The per-point active highlight is PER-LINE (background band on the li); the li's are the
// next/prev scroll anchors. A flagged bullet with NO position (deleted from the live CV since the read) renders
// after the list as an unmarked red line — never an invented number.
function RolePointList({ dbs, active, rowRefs, savedText, stickyH }: {
  dbs: DisplayBullet[]; active: string | null; rowRefs: MutableRefObject<Record<string, HTMLElement | null>>;
  savedText: Record<string, string>; stickyH: number;
}) {
  const numbered = dbs.filter((d) => d.num != null);
  const unnumbered = dbs.filter((d) => d.num == null);
  const line = (d: DisplayBullet) => {
    const flagged = !!d.weak;
    return (
      <span
        style={{ color: flagged ? 'var(--warning, #8A5A00)' : 'var(--ink)' }}
        data-testid="quality-bullet-text"
        data-point-flagged={flagged || undefined}
      >
        {flagged ? (savedText[d.bullet_id] ?? d.bullet_text) : d.bullet_text}
      </span>
    );
  };
  return (
    <>
      <ol style={S.bulletList} data-testid="quality-point-list">
        {numbered.map((d) => {
          const id = d.bullet_id;
          const flagged = !!d.weak;
          const isActive = active === id;
          return (
            <li
              key={id}
              value={d.num}
              ref={flagged ? (el) => { rowRefs.current[id] = el; } : undefined}
              style={{ ...S.bulletLi, ...(flagged ? S.bulletLiWeak : {}), ...(isActive ? S.bulletLiActive : {}), scrollMarginTop: stickyH }}
              data-testid={`quality-gutter-${id}`}
              data-flagged={flagged || undefined}
              data-active={isActive || undefined}
            >
              {line(d)}
            </li>
          );
        })}
      </ol>
      {unnumbered.map((d) => (
        <p
          key={d.bullet_id}
          ref={(el) => { rowRefs.current[d.bullet_id] = el; }}
          style={{ ...S.bulletText, ...(active === d.bullet_id ? S.bulletLiActive : {}), color: 'var(--warning, #8A5A00)', scrollMarginTop: stickyH }}
          data-testid={`quality-gutter-${d.bullet_id}`}
          data-flagged="true"
          data-active={active === d.bullet_id || undefined}
        >
          {line(d)}
        </p>
      ))}
    </>
  );
}

function SectionReview({ s, cvId, onPaywall, stale, onSaveBullet, entryFactsLink, antiFabrication = true, collapseAfter = COLLAPSE_AFTER }: { s: SectionQuality; cvId: string; onPaywall?: (cvId: string) => void; stale?: boolean; onSaveBullet?: SaveBullet; entryFactsLink?: EntryFactsLink; antiFabrication?: boolean; collapseAfter?: number }) {
  const items = s.items ?? [];
  const narrow = useIsNarrow(L2_STACK_WIDTH);
  // SHOW-ALL (owner ruling 2026-08-08) — the server now ships EVERY flagged entry; overwhelm-prevention moved here.
  // The first 3 entries render fully expanded exactly as before; every further entry renders behind a PERSISTENT
  // disclosure control (role title + a one-line signal + chevron) that expands the identical full presentation IN
  // PLACE — and can re-collapse it. NOT an exclusive accordion: toggling one never touches another. The control
  // stays MOUNTED across the toggle (Sol round-1 blocker 2): keyboard focus never falls to body, and aria-expanded
  // genuinely transitions false → true on the same element.
  const [userExpanded, setUserExpanded] = useState<Set<string>>(new Set());
  const isEntryOpen = (entryId: string, idx: number) => idx < collapseAfter || userExpanded.has(entryId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savedText, setSavedText] = useState<Record<string, string>>({});
  const [active, setActive] = useState<string | null>(null);   // the scroll shortcut's transient row highlight
  const rowRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollTo = useRef<string | null>(null);
  // STICKY HEADER (owner: "keep the heading section fixed for each view … persists the header and scrolls the entries.
  // if i go to research, the title and summary persists, while the details/entries are scrollable"). The section title
  // + the "Do this first" summary pin as ONE opaque block; everything else scrolls beneath. We MEASURE the block's real
  // rendered height (title + up-to-2 clamped summary lines are variable) and feed it to the entry rows' scroll-margin-top
  // so the next/prev jump never lands a row hidden behind the pinned block.
  const stickyRef = useRef<HTMLDivElement | null>(null);
  const [stickyH, setStickyH] = useState(0);
  useLayoutEffect(() => {
    const el = stickyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') { if (el) setStickyH(el.offsetHeight); return; }
    const ro = new ResizeObserver(() => setStickyH(el.offsetHeight));
    ro.observe(el);
    setStickyH(el.offsetHeight);
    return () => ro.disconnect();
  }, [s.section, s.recommendation]);

  const strong = s.strongEntries ?? [];
  const labels = sectionEntryLabels(s); // org-qualified headings, shared by the items, the strong entries + the pointer
  // IMG title-annotation DEDUP — computed over THIS surface's entries in document order (flagged items
  // first, then strong entries — matching render order), keyed by entry_id. Scope = this SectionReview
  // only; the drawer computes its own. Recomputes whenever the section data changes.
  const titleNotes = useMemo(() => {
    const ordered = [
      ...items.map((it) => ({ id: it.entry_id, title: it.entry_title })),
      ...(s.strongEntries ?? []).map((e) => ({ id: e.entry_id, title: e.entry_title })),
    ];
    const notes = annotateTitles(ordered.map((e) => e.title));
    return new Map(ordered.map((e, i) => [e.id, notes[i]] as const));
  }, [items, s.strongEntries]);
  const prompts = s.standard?.prompts ?? [];
  // FIX 1 — the section's accuracy checks, indexed by the bullet they belong to. A check whose bullet is ALSO an
  // impact finding renders inside that bullet's existing right cell (beside its own bullet, in the row-aligned grid);
  // the rest get their own row-aligned blocks below, so every check is beside its own bullet either way.
  const cautions = s.integrityCautions ?? [];
  const cautionsByBullet = useMemo(() => {
    const m = new Map<string, QIntegrityCaution[]>();
    for (const c of cautions) m.set(c.bullet_id, [...(m.get(c.bullet_id) ?? []), c]);
    return m;
  }, [cautions]);
  // SHOW-ALL — TWO id sets with DIFFERENT jobs (Sol round-1 blocker 1: they must never be conflated).
  // OWNERSHIP (all items, expanded or collapsed): a caution whose bullet belongs to ANY entry renders INSIDE that
  // entry (beside its bullet, via cautionsByBullet) and is therefore never "loose" — while the entry is collapsed it
  // simply stays unrendered until expand, exactly like the entry's other content. Filtering looseness on the
  // navigation set would leak a collapsed entry's caution into the loose block, breaking the collapsed-content
  // contract (collapsed = surfaced only on expand).
  const ownedBulletIds = useMemo(() => new Set(items.flatMap((it) => (it.bullets ?? []).map((b) => b.bullet_id))), [items]);
  // NAVIGATION (expanded items only), in DOCUMENT ORDER — the row order and the shortcut order: next/prev walks the
  // EXPANDED entries' points only; expanding a collapsed entry adds its points to the walk (in place, in order). The
  // shortcut never silently expands a collapsed entry.
  const flaggedIds = useMemo(
    () => items.flatMap((it, i) => (isEntryOpen(it.entry_id, i) ? (it.bullets ?? []).map((b) => b.bullet_id) : [])),
    [items, userExpanded],
  );
  const looseCautions = cautions.filter((c) => !ownedBulletIds.has(c.bullet_id));
  const looseVerify = looseCautions.filter((c) => c.urgency === 'verify');
  const looseConfirm = looseCautions.filter((c) => c.urgency !== 'verify');

  // The QUIET scroll shortcut moves the VIEWPORT, never the layout. Requested only on a deliberate click (not on
  // every render), and it honours prefers-reduced-motion.
  useEffect(() => {
    const id = scrollTo.current;
    if (!id) return;
    scrollTo.current = null;
    rowRefs.current[id]?.scrollIntoView?.({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  });

  // The per-entry anchors, kept: the section-to-section nav and the row walk both scroll to entries.
  const entryRefs = useRef<Record<string, HTMLElement | null>>({});

  const activeIdx = active ? flaggedIds.indexOf(active) : -1;
  function jump(delta: number) {
    if (flaggedIds.length === 0) return;
    const from = activeIdx < 0 ? (delta > 0 ? -1 : flaggedIds.length) : activeIdx;
    const next = Math.max(0, Math.min(flaggedIds.length - 1, from + delta));
    const id = flaggedIds[next];
    if (!id) return;
    setActive(id);
    scrollTo.current = id;
  }

  // The grid: two columns side by side, ONE column stacked. Explicit gridRow on both cells of a pair is what makes
  // the alignment genuine — the cells share a row edge instead of two columns drifting apart.
  const gridStyle: CSSProperties = narrow
    ? { ...S.grid, gridTemplateColumns: '1fr' }
    : { ...S.grid, gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 1fr)' };
  const leftRowOf = (i: number) => (narrow ? i * 2 + 1 : i + 1);
  const rightRowOf = (i: number) => (narrow ? i * 2 + 2 : i + 1);

  return (
    <div data-testid="quality-l2">
      {/* The per-role feedback scroller's quiet, on-theme scrollbar affordance (thin, --rule). It appears only when the
          box actually overflows (2+ items), so a single-item box shows no false affordance. */}
      <style>{`.cvc-fb-scroll{scrollbar-width:thin;scrollbar-color:var(--rule) transparent}.cvc-fb-scroll::-webkit-scrollbar{width:8px}.cvc-fb-scroll::-webkit-scrollbar-thumb{background:var(--rule);border-radius:4px}.cvc-fb-scroll::-webkit-scrollbar-track{background:transparent}`}</style>
      {/* THE PINNED HEADER — section title + the "Do this first" summary, as ONE opaque sticky block. `top: 0` because
          the app header (App.tsx S.appHeader) is a NORMAL in-flow block, not fixed/sticky, so nothing occupies the
          viewport top for us to offset past. Opaque `--canvas` background so entry rows never bleed through; a single
          hairline `--rule` bottom border separates it from the scrolling content. z-index 20 sits ABOVE the entry rows
          (which use z-index 0/1 inside the grid) and BELOW the CvFullDrawer button (30), the Drawer (40) and the
          ModalShell (50). The whole block is capped at max-height 45vh (VIEWPORT GUARD) so a long summary can never
          swallow the screen, and the summary itself is clamped to 2 lines. */}
      <div ref={stickyRef} style={S.l2StickyHeader} data-testid="quality-l2-sticky">
        <h1 style={S.l2Title}>{s.section}</h1>
        {s.recommendation && (
          <p
            style={S.sectionRecSticky}
            data-testid="quality-recommendation"
            title={`Do this first: ${markStaleEndorsement(tightenRecommendation(s.recommendation, firstFlaggedTitleOf(items, labels)), stale)}`}
          >
            <strong>Do this first: </strong>{markStaleEndorsement(tightenRecommendation(s.recommendation, firstFlaggedTitleOf(items, labels)), stale)}
          </p>
        )}
      </div>
      {/* THE SECTION INDEX IS GONE (owner, 2026-08-23, on a real OB/GYN CV: "this is so disorganized, i dont even
          know what is relevant to me as a user… the left-right entry by entry review is what it is meant to be").
          It was added a day earlier as an organiser and it became the FIRST of three stacked renderings of the
          same entries — index rows, then the entry rows, then every entry again as a fact card below. The
          left-right rows below ARE the reading layer; an index above them is a second copy of it, not a way in. */}
      {/* ANTI-FABRICATION — a drafting warning, so it renders only where drafting happens. On the rubric surface
          it is suppressed entirely (`antiFabrication={false}`): the engine's rows are clarification prompts, the
          applicant is not being invited to write numbers here, and a standing red banner above every section was
          exactly the "talking to yourself, not the user" copy the owner asked us to cut. It still renders beside
          an OPEN improve editor (see BulletReview), which is the moment it is actually about. */}
      {antiFabrication && flaggedIds.length > 0 && (
        <p style={S.antiFabTop} data-testid="quality-antifab">{ANTI_FABRICATION}</p>
      )}
      {/* The row walk needs TWO rows to walk between (Sol round 2, 2026-08-23: on a single point it is two mostly
          disabled controls — clutter, not an affordance). A one-point section is served by the index row above. */}
      {flaggedIds.length > 1 && (
        <div style={S.guided} data-testid="quality-guided">
          {/* QUIET scroll shortcuts — optional aids, NOT the navigation model: every row is already on the page. */}
          <span style={S.guidedHint}>Every line and its review are on this page — these move the highlight from row to row.</span>
          <span style={S.guidedNav}>
            <button className="btn btn-ghost" style={S.guidedBtn} onClick={() => jump(-1)} disabled={activeIdx <= 0} data-testid="quality-prev-issue">← Previous</button>
            <button className="btn btn-ghost" style={S.guidedBtn} onClick={() => jump(1)} disabled={activeIdx === flaggedIds.length - 1} data-testid="quality-next-issue">Next →</button>
          </span>
        </div>
      )}
      <div data-testid="quality-l2-content">
        <p style={S.paneHead}>Your {s.section} entries</p>
        {items.map((it, itemIdx) => {
          // SHOW-ALL — an entry beyond the first 3 gets a PERSISTENT disclosure control (role title + "N points to
          // review" + chevron): one <button> that stays mounted whether the entry is open or closed, toggling the
          // identical full presentation in place. Persistent-by-design (Sol round-1 blocker 2): keyboard focus stays
          // on the control across activation, aria-expanded transitions on the same element, and users can
          // RE-collapse an entry they opened. aria-controls names the content region rendered while open.
          const collapsible = itemIdx >= collapseAfter;
          const open = isEntryOpen(it.entry_id, itemIdx);
          const nPoints = it.bullets?.length ?? 0;
          const heading = collapsible ? (
            <button
              type="button"
              className="btn btn-ghost"
              style={S.collapsedRow}
              aria-expanded={open}
              aria-controls={open ? `quality-entry-${it.entry_id}` : undefined}
              data-testid={`quality-collapsed-${it.entry_id}`}
              onClick={() => setUserExpanded((prev) => {
                const next = new Set(prev);
                if (next.has(it.entry_id)) next.delete(it.entry_id); else next.add(it.entry_id);
                return next;
              })}
            >
              <span style={S.collapsedTitle}>{labels.get(it.entry_id) ?? it.entry_title}</span>
              <span style={S.collapsedSignal}>
                {nPoints === 1 ? COPY['CS-POINT-TO-CHECK-ONE'] : COPY['CS-POINTS-TO-CHECK'].replace('{n}', String(nPoints))}
                <span aria-hidden="true" style={S.collapsedChevron}>{open ? '▴' : '▾'}</span>
              </span>
            </button>
          ) : (
            it.entry_title ? <p style={S.roleTitle}>{labels.get(it.entry_id) ?? it.entry_title}</p> : null
          );
          if (!open) {
            return (
              <div key={it.entry_id} style={S.role} data-testid="quality-item-collapsed">
                {heading}
              </div>
            );
          }
          // L2 point numbering — the role's FULL display list (clean siblings included when the serve-time
          // enrichment carried them), numbered by TRUE position. On the stacked path each display row's grid start
          // row is precomputed: a clean sibling occupies ONE grid row, a flagged pair occupies TWO.
          const dbs = displayBulletsOf(it);
          const rowStart: number[] = [];
          let nextRow = 1;
          for (const d of dbs) { rowStart.push(nextRow); nextRow += d.weak ? 2 : 1; }
          const numOf = new Map(dbs.filter((d) => d.num != null).map((d) => [d.bullet_id, d.num!] as const));
          // OWNER RULING (2026-08-08) — ATOMIC roles render the COMPACT pre-split list (RolePointList: one tight
          // <ol>, red/black lines, true numbering, per-line active highlight). A role still carrying a LEGACY
          // multi-line bullet keeps the per-cell path below UNCHANGED (its BulletText <ol> numbers its own lines —
          // the two numbering schemes must never meet on one role).
          const legacyRole = dbs.some((d) => !isAtomicBullet(d.bullet_text));
          if (!legacyRole) {
            const cards = (it.bullets ?? []).map((b, i) => {
              const id = b.bullet_id;
              const isActive = active === id;
              const num = numOf.get(id);
              return (
                <div
                  key={id}
                  style={{ ...S.feedbackItem, ...(i > 0 ? S.feedbackItemDivided : {}), ...(isActive ? S.feedbackItemActive : {}) }}
                  data-testid={`quality-feedback-${id}`}
                  data-active={isActive || undefined}
                >
                  {/* "Point N" — the bullet's TRUE position, same map as the left list (bullet_id correspondence,
                      never text parsing). A position-less (deleted-since-read) bullet gets no invented number. */}
                  {num != null && <span style={S.pointLabel} data-testid={`quality-point-label-${id}`}>Point {num}</span>}
                  <BulletReview
                    b={b}
                    prompts={prompts}
                    cvId={cvId}
                    onPaywall={onPaywall}
                    onSave={onSaveBullet}
                    draft={drafts[id] ?? savedText[id] ?? b.bullet_text}
                    onDraft={(t) => setDrafts((dr) => ({ ...dr, [id]: t }))}
                    saved={savedText[id] !== undefined}
                    onSaved={(t) => setSavedText((m) => ({ ...m, [id]: t }))}
                  />
                  {(cautionsByBullet.get(id) ?? []).map((c) => (
                    <IntegrityCaution key={`${c.bullet_id}-${c.gate_id}`} c={c} cvId={cvId} />
                  ))}
                </div>
              );
            });
            return (
              <div key={it.entry_id} style={S.role} data-testid="quality-item">
                {/* Collapsible: the toggle sits OUTSIDE the region it aria-controls. Non-collapsible: the plain
                    roleTitle sits INSIDE the (id-less) wrapper so the title-annotation's previous-sibling adjacency
                    (a pinned surface) is byte-identical to the pre-lift DOM. */}
                {collapsible && heading}
                <div id={collapsible ? `quality-entry-${it.entry_id}` : undefined}>
                {!collapsible && heading}
                <TitleAnnotationNote entryId={it.entry_id} note={titleNotes.get(it.entry_id) ?? null} />
                {narrow ? (
                  /* NARROW — the compact list first, then the role's feedback cards stacked beneath (no bounded
                     scroller on narrow, as before). The per-line + per-card active highlights carry the pairing. */
                  <div data-testid={`quality-rows-${it.entry_id}`}>
                    <RolePointList dbs={dbs} active={active} rowRefs={rowRefs} savedText={savedText} stickyH={stickyH} />
                    <div>{cards}</div>
                  </div>
                ) : (
                  /* DESKTOP — compact list LEFT (fixed context), the bounded feedback scroller RIGHT, unchanged. */
                  <div style={S.roleGrid} data-testid={`quality-rows-${it.entry_id}`}>
                    <div style={S.roleLeftCol}>
                      <RolePointList dbs={dbs} active={active} rowRefs={rowRefs} savedText={savedText} stickyH={stickyH} />
                    </div>
                    <div
                      className="cvc-fb-scroll"
                      style={S.feedbackScroller}
                      data-testid={`quality-feedback-scroller-${it.entry_id}`}
                      data-scroller="true"
                    >
                      {cards}
                    </div>
                  </div>
                )}
                </div>
                <EntryFactsLinkLine link={entryFactsLink} entryId={it.entry_id} />
              </div>
            );
          }
          return (
          <div key={it.entry_id} style={S.role} data-testid="quality-item">
            {/* same collapsible-vs-plain heading placement as the atomic branch above */}
            {collapsible && heading}
            <div id={collapsible ? `quality-entry-${it.entry_id}` : undefined}>
            {!collapsible && heading}
            <TitleAnnotationNote entryId={it.entry_id} note={titleNotes.get(it.entry_id) ?? null} />
            {narrow ? (
            <div style={gridStyle} data-testid={`quality-rows-${it.entry_id}`}>
              {dbs.map((d, di) => {
                const id = d.bullet_id;
                const b = d.weak;
                const start = rowStart[di];
                // CLEAN sibling — display context only ("black for no issues"): numbered by true position, normal
                // ink, NO feedback row and NO anchor (next/prev walks flagged bullets only).
                if (!b) return (
                  <div key={id} style={{ ...S.cellLeft, gridRow: start, gridColumn: 1 }} data-testid={`quality-gutter-${id}`}>
                    <BulletText text={d.bullet_text} num={d.num} />
                  </div>
                );
                const isActive = active === id;
                const atomic = isAtomicBullet(b.bullet_text);
                return (
                  <Fragment key={id}>
                    {/* OWNER AMENDMENT 2026-08-04: "the active row highlight should be uniform on the row, its length
                        decided by the longest length between the review. so we have one uniform highlight from left to
                        right." ONE continuous band per active row — a BACKGROUND GRID ITEM occupying the SAME grid
                        row(s) as the pair and ALL columns (`1 / -1`), so it spans the column gap too and its height is
                        the row track's height (the taller of the two cells). Stacked (<700px) it spans BOTH of the
                        pair's rows, wrapping bullet + feedback as one shaded block. The cells keep their own layout —
                        the band sits behind them (zIndex 0 vs the cells' 1), so top-alignment and the all-visible
                        model are untouched. */}
                    {isActive && (
                      <div
                        aria-hidden="true"
                        data-testid={`quality-row-band-${id}`}
                        data-row={d.num ?? di + 1}
                        style={{
                          ...S.rowBand,
                          gridColumn: '1 / -1',
                          gridRow: `${start} / ${start + 2}`,
                        }}
                      />
                    )}
                    <div
                      ref={(el) => { rowRefs.current[id] = el; }}
                      style={{ ...S.cellLeft, ...S.cellFlagged, ...(isActive ? S.cellActive : {}), gridRow: start, gridColumn: 1, scrollMarginTop: stickyH }}
                      data-testid={`quality-gutter-${id}`}
                      data-flagged="true"
                      data-active={isActive || undefined}
                      data-row={d.num ?? di + 1}
                    >
                      <BulletText text={savedText[id] ?? b.bullet_text} weakLines={savedText[id] ? [] : (b.weak_lines ?? [])} num={d.num} flagged />
                    </div>
                    <div
                      style={{ ...S.cellRight, ...S.cellRightStacked, gridRow: start + 1, gridColumn: 1 }}
                      data-testid={`quality-feedback-${id}`}
                      data-active={isActive || undefined}
                      data-row={d.num ?? di + 1}
                    >
                      {/* "Point N" — the bullet's TRUE position (same map, same bullet_id as the left cell).
                          Suppressed for a LEGACY multi-line bullet, whose diagnosis numbers its own lines instead. */}
                      {atomic && d.num != null && <span style={S.pointLabel} data-testid={`quality-point-label-${id}`}>Point {d.num}</span>}
                      <BulletReview
                        b={b}
                        prompts={prompts}
                        cvId={cvId}
                        onPaywall={onPaywall}
                        onSave={onSaveBullet}
                        draft={drafts[id] ?? savedText[id] ?? b.bullet_text}
                        onDraft={(t) => setDrafts((dr) => ({ ...dr, [id]: t }))}
                        saved={savedText[id] !== undefined}
                        onSaved={(t) => setSavedText((m) => ({ ...m, [id]: t }))}
                      />
                      {/* FIX 1 — this bullet's accuracy checks, beside this bullet, SEPARATE from its impact review. */}
                      {(cautionsByBullet.get(id) ?? []).map((c) => (
                        <IntegrityCaution key={`${c.bullet_id}-${c.gate_id}`} c={c} cvId={cvId} />
                      ))}
                    </div>
                  </Fragment>
                );
              })}
            </div>
            ) : (
            /* DESKTOP/TABLET — the CV bullets stay in normal flow on the LEFT (fixed context, top-aligned); this role's
               feedback items stack inside ONE bounded, independently-scrollable box on the RIGHT (the outer page scroll
               still pins the header). The box shows ~2 items and shrinks to fit a single one — never an empty gap. */
            <div style={S.roleGrid} data-testid={`quality-rows-${it.entry_id}`}>
              <div style={S.roleLeftCol}>
                {dbs.map((d) => {
                  const id = d.bullet_id;
                  const b = d.weak;
                  // CLEAN sibling — black, numbered by true position, not an anchor.
                  if (!b) return (
                    <div key={id} style={S.cellLeft} data-testid={`quality-gutter-${id}`}>
                      <BulletText text={d.bullet_text} num={d.num} />
                    </div>
                  );
                  const isActive = active === id;
                  return (
                    <div
                      key={id}
                      ref={(el) => { rowRefs.current[id] = el; }}
                      style={{ ...S.cellLeft, ...S.cellFlagged, ...(isActive ? S.cellActive : {}), scrollMarginTop: stickyH }}
                      data-testid={`quality-gutter-${id}`}
                      data-flagged="true"
                      data-active={isActive || undefined}
                    >
                      <BulletText text={savedText[id] ?? b.bullet_text} weakLines={savedText[id] ? [] : (b.weak_lines ?? [])} num={d.num} flagged />
                    </div>
                  );
                })}
              </div>
              <div
                className="cvc-fb-scroll"
                style={S.feedbackScroller}
                data-testid={`quality-feedback-scroller-${it.entry_id}`}
                data-scroller="true"
              >
                {(it.bullets ?? []).map((b, i) => {
                  const id = b.bullet_id;
                  const isActive = active === id;
                  const num = numOf.get(id);
                  return (
                    <div
                      key={id}
                      style={{ ...S.feedbackItem, ...(i > 0 ? S.feedbackItemDivided : {}), ...(isActive ? S.feedbackItemActive : {}) }}
                      data-testid={`quality-feedback-${id}`}
                      data-active={isActive || undefined}
                    >
                      {/* "Point N" — the bullet's TRUE position, from the SAME display map the left column used
                          (bullet_id correspondence, never text parsing). Suppressed for a legacy multi-line bullet
                          (its diagnosis numbers its own lines). */}
                      {isAtomicBullet(b.bullet_text) && num != null && <span style={S.pointLabel} data-testid={`quality-point-label-${id}`}>Point {num}</span>}
                      <BulletReview
                        b={b}
                        prompts={prompts}
                        cvId={cvId}
                        onPaywall={onPaywall}
                        onSave={onSaveBullet}
                        draft={drafts[id] ?? savedText[id] ?? b.bullet_text}
                        onDraft={(t) => setDrafts((dr) => ({ ...dr, [id]: t }))}
                        saved={savedText[id] !== undefined}
                        onSaved={(t) => setSavedText((m) => ({ ...m, [id]: t }))}
                      />
                      {(cautionsByBullet.get(id) ?? []).map((c) => (
                        <IntegrityCaution key={`${c.bullet_id}-${c.gate_id}`} c={c} cvId={cvId} />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
            )}
            </div>
            <EntryFactsLinkLine link={entryFactsLink} entryId={it.entry_id} />
          </div>
          );
        })}
        {/* FIX 2 — the display cap, disclosed HERE TOO. L2 is where a user works through the entries and concludes
            "that's the section done" — the surface where an undisclosed cap does its damage. */}
        <CapDisclosure n={s.suppressedItems ?? 0} tid="l2" />
        {flaggedIds.length === 0 && cautions.length === 0 && (
          <p style={S.q}>Nothing in this section was flagged — there is no issue to work through here.</p>
        )}
        {/* FIX 1 — accuracy checks on bullets that carry NO impact finding. They still render ROW-ALIGNED, each beside
            its OWN bullet, in two groups: the 'verify' band (a concrete status/venue discrepancy — worth checking
            first) and the calmer pre-send 'confirm' band (contribution/framing wording). Neither group is a finding,
            and neither changes this section's rating. */}
        {looseVerify.length > 0 && (
          <LooseCautionBlock
            heading="Check these before you send"
            note="These are automated accuracy checks on wording that may state a status or venue inexactly — not findings, and they do not change this section's rating."
            testId="quality-checks-verify"
            cautions={looseVerify} cvId={cvId} gridStyle={gridStyle} leftRowOf={leftRowOf} rightRowOf={rightRowOf} narrow={narrow}
          />
        )}
        {looseConfirm.length > 0 && (
          <LooseCautionBlock
            heading="Before sending — wording to confirm"
            note="These are automated wording checks — not findings, and they do not change this section's rating. If the wording is already exact, dismiss them."
            testId="quality-checks-confirm"
            cautions={looseConfirm} cvId={cvId} gridStyle={gridStyle} leftRowOf={leftRowOf} rightRowOf={rightRowOf} narrow={narrow}
          />
        )}
        {/* UNFLAGGED bullets render LEFT-CELL-ONLY, at reduced emphasis — no empty right boxes. The gutter-mark
            language for a strong line is kept. */}
        {strong.length > 0 && (
          <div data-testid="quality-l2-strong">
            {strong.map((e, i) => (
              // The section index jumps here directly: an entry with nothing flagged has no bullet row to highlight.
              <div key={e.entry_id} style={{ ...S.role, scrollMarginTop: stickyH }} ref={(el) => { entryRefs.current[e.entry_id] = el; }}>
                {e.entry_title && <p style={S.roleTitle}>{labels.get(e.entry_id) ?? e.entry_title}</p>}
                <TitleAnnotationNote entryId={e.entry_id} note={titleNotes.get(e.entry_id) ?? null} />
                {/* OWNER RULING 2026-08-08 — the strong entry's points as ONE compact numbered list too (the
                    pre-split look), inside the single green-edged cell the strong tier already used. All black. */}
                <div style={{ ...S.cellLeft, ...S.cellUnflagged }}>
                  <ol style={S.bulletList}>
                    {e.bullets.map((b, i) => (
                      <li key={b.bullet_id} value={i + 1} style={S.bulletLi} data-testid={`quality-gutter-${b.bullet_id}`}>
                        <span style={{ color: 'var(--ink)' }} data-testid="quality-bullet-text">{b.bullet_text}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <EntryFactsLinkLine link={entryFactsLink} entryId={e.entry_id} />
              </div>
            ))}
          </div>
        )}
      </div>
      {/* L3 — the section's standard, on demand, below the work (never above it). */}
      {s.standard && (
        <details style={S.detail} data-testid="quality-standard">
          <summary style={S.detailSummary}>What a strong {s.section} entry shows</summary>
          <p style={S.detailBody}>{s.standard.exemplar}</p>
        </details>
      )}
    </div>
  );
}

// IMG TITLE ANNOTATION — Tier 1 deterministic display layer (owner + Sol ratified, 2026-08-08). One quiet muted
// sentence under the role title explaining how a Canadian reviewer READS the title — informational only, NEVER a
// rename suggestion (a retitle nudge would push misrepresentation). Allowlist + copy contract live in
// lib/titleAnnotations.ts. Matches the entry TITLE only (never the org-suffixed display label, never bullets);
// no match → renders nothing.
// DEDUP (owner + Sol ratified, 2026-08-08): the note is computed ONCE per surface via annotateTitles
// over the section's entries in document order (SectionReview's titleNotes memo) — a rule fires its
// full note on the first matching entry only (plus a generic "applies below as well" suffix when the
// same rule matches again); later same-rule entries render nothing. Observership repeats on every
// match — never deduped.
function TitleAnnotationNote({ entryId, note }: { entryId: string; note: string | null }) {
  if (!note) return null;
  return <p style={S.titleAnnotation} data-testid={`title-annotation-${entryId}`}>{note}</p>;
}

// FIX 1 — a ROW-ALIGNED block of accuracy checks whose bullets carry no impact finding: the user's own bullet on the
// left, the caution on the right, sharing a row edge — the same correspondence model as the impact review, so a check
// is never printed away from the words it is about.
function LooseCautionBlock({ heading, note, testId, cautions, cvId, gridStyle, leftRowOf, rightRowOf, narrow }: {
  heading: string; note: string; testId: string; cautions: QIntegrityCaution[]; cvId: string;
  gridStyle: CSSProperties; leftRowOf: (i: number) => number; rightRowOf: (i: number) => number; narrow: boolean;
}) {
  return (
    <div style={S.role} data-testid={testId}>
      <p style={S.roleTitle}>{heading}</p>
      <p style={S.checksNote}>{note}</p>
      <div style={gridStyle}>
        {cautions.map((c, i) => (
          <Fragment key={`${c.bullet_id}-${c.gate_id}`}>
            <div
              style={{ ...S.cellLeft, ...S.cellCaution, gridRow: leftRowOf(i), gridColumn: 1 }}
              data-testid={`quality-check-bullet-${c.bullet_id}-${c.gate_id}`}
            >
              <BulletText text={c.bullet_text} />
            </div>
            <div
              style={{ ...S.cellRight, ...(narrow ? S.cellRightStacked : {}), gridRow: rightRowOf(i), gridColumn: narrow ? 1 : 2 }}
            >
              <IntegrityCaution c={c} cvId={cvId} />
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// ONE bullet's RIGHT CELL: its diagnosis, the question it raises, and the improve affordance — nothing else. The
// section standard and the teaching content stay in the L3 expanders, so the cell stays compact enough that every
// row can be on screen at once. There is no group note and no member stepper: with all rows visible there is nothing
// to step between. The ANTI-FABRICATION warning renders here whenever the editor is OPEN (it also renders once at the
// top of the section) — so the user never drafts without it in view.
function BulletReview({ b, prompts, cvId, onPaywall, onSave, draft, onDraft, saved, onSaved }: {
  b: QWeakBullet;
  prompts: PromptChip[]; cvId: string; onPaywall?: (cvId: string) => void; onSave?: SaveBullet;
  draft: string; onDraft: (t: string) => void; saved: boolean; onSaved: (t: string) => void;
}) {
  const [revising, setRevising] = useState(false);
  const [note, setNote] = useState<'paywall' | 'error' | null>(null);
  const [ticked, setTicked] = useState<string[]>([]);
  function toggle(c: string) { setTicked((t) => (t.includes(c) ? t.filter((x) => x !== c) : [...t, c])); }
  const asked = prompts.filter((p) => ticked.includes(p.chip));
  async function save() {
    setNote(null);
    if (!onSave) return;
    // FIREWALL: send ONLY the user's typed text — never any composed/appended string.
    const r = await onSave(b.bullet_id, draft);
    if (r.status === 402) { setNote('paywall'); onPaywall?.(cvId); return; }
    if (!r.ok) { setNote('error'); return; }
    onSaved(draft);
  }
  return (
    <div style={S.bullet} data-testid="quality-bullet">
      {saved ? (
        <p style={S.assessAddressed} data-testid="quality-bullet-diag">
          <span style={{ textDecoration: 'line-through' }}>{b.diagnosis}</span> — Addressed — re-run the read to re-check.
        </p>
      ) : (
        <>
          {/* cc-10 ITEM 6 — the adequacy finding's disposition. Rendered only on the UNADDRESSED state: once the user
              has saved a revision the card already says "Addressed", and an instruction to sharpen beside it would
              contradict that. */}
          <DispositionLabel text={DISPOSITION_SHARPEN} testid={`quality-disposition-sharpen-${b.bullet_id}`} />
          <p style={S.assessLine} data-testid="quality-bullet-diag">{b.diagnosis}</p>
          <p style={S.q}>{b.question}</p>
          {/* cc-7 (Sol round-4 design amendment): the quantity ESCAPE ROUTE is deterministic UI copy — a fixed
              hint under every quantity question, instead of two-clause phrasing woven into the question itself.
              Over-detection is harmless: the hint is a reassurance line that instructs the answerer. */}
          {isQuantityAsk(b.question) && (
            <p style={S.quantityHint} data-testid="quantity-hint">{QUANTITY_HINT_COPY}</p>
          )}
        </>
      )}
      {!revising ? (
        !saved && onSave && (
          <button className="btn btn-ghost" style={S.reviseBtn} onClick={() => setRevising(true)} data-testid="quality-revise">Improve with prompts ✎</button>
        )
      ) : (
        <div style={S.revise}>
          {prompts.length > 0 && (
            <div style={S.jog} data-testid="quality-jog">
              {prompts.map((p) => (
                <label key={p.chip} style={{ ...S.chip, ...(ticked.includes(p.chip) ? S.chipOn : {}) }}>
                  <input type="checkbox" checked={ticked.includes(p.chip)} onChange={() => toggle(p.chip)} style={{ marginRight: 6 }} data-testid={`quality-chip-${p.chip.replace(/\W+/g, '-').replace(/^-|-$/g, '').toLowerCase()}`} />
                  {p.chip}
                </label>
              ))}
            </div>
          )}
          {asked.length > 0 && (
            <div style={S.prompt} data-testid="quality-prompt">
              <p style={S.promptLead}>A strong version answers:</p>
              <ul style={S.promptList}>{asked.map((p) => <li key={p.chip}>{p.question}</li>)}</ul>
            </div>
          )}
          {/* the warning is BESIDE the OPEN editor — visible whenever the user is drafting */}
          <p style={S.antiFab} data-testid="quality-antifab">{ANTI_FABRICATION}</p>
          <textarea style={S.ta} rows={4} value={draft} onChange={(e) => { onDraft(e.target.value); setNote(null); }} data-testid="quality-edit" />
          <button className="btn btn-secondary" style={S.save} onClick={save} disabled={saved || !draft.trim()} data-testid="quality-save">{saved ? <><IconCheck aria-hidden="true" /> Saved</> : 'Save my revision'}</button>
          {note === 'paywall' && <p style={S.reviseNote} data-testid="quality-revise-note">This CV needs its unlock to save — your words are kept.</p>}
          {note === 'error' && <p style={S.reviseNote} data-testid="quality-revise-note">Something went wrong saving — try again.</p>}
        </div>
      )}
    </div>
  );
}

// The user's bullet shown read-only. A multi-sentence bullet renders as NUMBERED LINES (display split of their own
// verbatim words — no composition); the duty lines (weak_lines, from the classifier) are marked so the diagnosis's
// "Lines 2, 4 read as duties" lands on the lines the user sees. Never a write path.
//
// REGRESSION FIX (owner, 2026-08-08): the atomic split made each point a SINGLE-LINE bullet, which used to fall
// into the plain-<p> path below — losing the numbering and the red/black colour code the old multi-line <ol> gave
// every point. `num` restores the per-role number ("1.", "2." — display-only, derived by the CALLER from rendered
// order, never from model text) and `flagged` restores the colour code (an atomic flagged point renders in the
// same var(--warning) red the old weak lines used; clean stays normal ink). The multi-line path is UNTOUCHED —
// legacy un-re-run bullets keep the exact pre-split look, including their internal weak_lines numbering.
// An ATOMIC (post-split) bullet is single-line under the same display split BulletText uses — the discriminator for
// "outer Point-N numbering applies" vs "legacy multi-line bullet keeps its internal line numbering".
function isAtomicBullet(text: string): boolean { return splitIntoLines(text).length <= 1; }

function BulletText({ text, weakLines = [], num, flagged = false }: { text: string; weakLines?: number[]; num?: number; flagged?: boolean }) {
  const lines = splitIntoLines(text);
  if (lines.length <= 1) return (
    <p style={{ ...S.bulletText, ...(flagged ? S.bulletTextFlagged : {}) }} data-testid="quality-bullet-text" data-point-flagged={flagged || undefined}>
      {num != null && <span style={S.pointNum} data-testid="quality-point-num">{num}.</span>}
      {text}
    </p>
  );
  const weak = new Set(weakLines);
  return (
    <ol style={S.bulletList} data-testid="quality-bullet-text">
      {lines.map((l, i) => {
        const isWeak = weak.has(i + 1);
        return (
          <li key={i} style={{ ...S.bulletLi, ...(isWeak ? S.bulletLiWeak : {}) }} data-weak={isWeak || undefined}>
            {l}{isWeak && <span style={S.weakTag}> — reads as a duty</span>}
          </li>
        );
      })}
    </ol>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 'min(1080px, 94vw)', margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  back: { marginLeft: 0, paddingLeft: 0, fontSize: 'var(--fs-small)', marginBottom: 'var(--space-2)' },
  // The re-run + timestamp control row, kept at the TOP under the h1 (owner ask #1). Single column, no split.
  rerunRowTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap', margin: 'var(--space-2) 0 var(--space-3)' },
  intro: { color: 'var(--muted)', margin: 'var(--space-1) 0 var(--space-3)' },
  scopeNote: { color: 'var(--muted)', fontSize: 'var(--fs-label)', fontStyle: 'italic', margin: '0 0 var(--space-4)' },
  overall: { margin: 'var(--space-2) 0 var(--space-4)', fontWeight: 600, fontSize: 'var(--fs-h3)', color: 'var(--ink)', lineHeight: 1.45 },
  // L1 — the compact section card. The "Start here" card carries the only accent on the overview.
  card: { background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 8, margin: 'var(--space-2) 0', overflow: 'hidden' },
  cardStart: { borderColor: 'var(--ink)', borderLeftWidth: 3 },
  cardHead: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' },
  headRight: { display: 'flex', alignItems: 'center', gap: 'var(--space-2)' },
  secName: { fontWeight: 600, color: 'var(--ink)', fontSize: 15 },
  startHere: { fontSize: 'var(--fs-micro)', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px var(--space-2)', borderRadius: 999, background: 'var(--ink)', color: 'var(--card, #fff)' },
  chev: { color: 'var(--muted)', fontSize: 'var(--fs-label)', whiteSpace: 'nowrap' },
  body: { padding: '0 var(--space-4) var(--space-4)' },
  plainVerdict: { margin: '0 0 var(--space-2)', fontSize: 15, fontWeight: 600 },
  canmedsCap: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-body)', color: 'var(--ink-subtle)', fontFamily: 'var(--serif)' },
  roleFrame: { margin: 'var(--space-2) 0 var(--space-1)', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink)' },
  verdict: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-body)', color: 'var(--ink)' },
  covWhy: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)' },
  evidence: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--muted, #777)' },
  // the integrity disclosure — LOUD by design (an actionable obligation), never folded into the quiet line above.
  integrity: { margin: '0 0 var(--space-3)', padding: 'var(--space-2) var(--space-3)', borderLeft: '3px solid var(--warning, #8A5A00)', background: 'var(--surface-subtle)', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink)' },
  // FIX 1 — the CALM pre-send wording-check line. Deliberately quieter than the 'verify' disclosure above it: a
  // contribution/framing reading is precision-prone and must not read like an adjudicated accusation.
  wordingCheck: { margin: '0 0 var(--space-3)', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)' },
  // FIX 1 — one automated accuracy caution. Neutral by default; the 'verify' band gets the warning edge.
  caution: { margin: '0 0 var(--space-3)', padding: 'var(--space-2) var(--space-3)', borderLeft: '3px solid var(--rule)', background: 'var(--surface-subtle)', borderRadius: 4, fontSize: 'var(--fs-small)' },
  cautionVerify: { borderLeftColor: 'var(--warning, #8A5A00)' },
  cautionHead: { margin: '0 0 var(--space-1)', fontWeight: 600, color: 'var(--ink)', lineHeight: 1.45 },
  cautionDiag: { margin: '0 0 var(--space-1)', color: 'var(--ink-subtle)', lineHeight: 1.45 },
  cautionQ: { margin: '0 0 var(--space-1)', color: 'var(--ink)', lineHeight: 1.45 },
  cautionDismiss: { fontSize: 'var(--fs-label)', marginLeft: 0, paddingLeft: 0 },
  cellCaution: { borderLeftColor: 'var(--rule)' },
  checksNote: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--muted, #777)', lineHeight: 'var(--lh-body)' },
  assessDetail: { margin: '0 0 var(--space-3)', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)' },
  standard: { background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 6, padding: 'var(--space-2) var(--space-3)', margin: '0 0 var(--space-4)' },
  standardLabel: { margin: '0 0 var(--space-1)', fontSize: 'var(--fs-label)', fontWeight: 600, color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.02em' },
  standardBody: { margin: 0, fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)', lineHeight: 'var(--lh-body)' },
  // the PRIMARY affordance — filled and full width at the card bottom.
  reviewBtn: { fontSize: 'var(--fs-body)', marginTop: 'var(--space-3)', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' },
  // L2 — INLINE ANNOTATION (single column at every viewport; the old split/tab styles are gone with the layout)
  // Inside the sticky block: no bottom margin (the block's padding + the summary provide the spacing).
  l2Title: { margin: 0, fontSize: 'var(--fs-h1)', color: 'var(--ink)' },
  // THE PINNED HEADER BLOCK. Opaque `--canvas` bg (rows never bleed through) + a hairline `--rule` bottom border.
  // `top: 0` — the app header is NOT fixed, so nothing sits above us. z-index 20: above the entry rows (0/1), below the
  // drawer (40) / drawer button (30) / modal (50). maxHeight 45vh is the hard VIEWPORT GUARD so a long summary can't
  // swallow the screen. The page stays the only scroll container — NO overflow on any ancestor.
  l2StickyHeader: {
    position: 'sticky', top: 0, zIndex: 20, background: 'var(--canvas)',
    borderBottom: '1px solid var(--rule)', maxHeight: '45vh', overflow: 'hidden',
    padding: 'var(--space-2) 0 var(--space-2)', margin: '0 0 var(--space-3)',
  },
  // The pinned "Do this first" summary, CLAMPED to 2 lines so a 2–3 line recommendation stays compact even at 375px.
  // The full text remains in the DOM node (line-clamp is a purely visual truncation — screen readers still read it all)
  // and is also exposed via the element's `title` tooltip.
  sectionRecSticky: {
    margin: 'var(--space-2) 0 0', padding: 'var(--space-2) var(--space-3)', borderLeft: '3px solid var(--ink)',
    background: 'var(--surface-subtle)', fontSize: 'var(--fs-body)', color: 'var(--ink)',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
  },
  paneHead: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-label)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--muted, #777)' },
  // THE SECTION INDEX — the L2 organiser. Uniform full-width rows (owner: "the active row highlight should be
  // uniform on the row … one uniform highlight from left to right"), separated by the same hairline the entry rows
  // use, each carrying its own status bar in the same green / amber the detail cells already use.
  // SECTION-TO-SECTION nav, directly under the way back to the overview.
  sectionNav: { display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)', margin: '0 0 var(--space-3)' },
  indexWrap: { margin: '0 0 var(--space-4)', border: '1px solid var(--rule)', borderRadius: 6, overflow: 'hidden', background: 'var(--surface-subtle)' },
  indexRow: { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'baseline', width: '100%', textAlign: 'left', justifyContent: 'flex-start', margin: 0, padding: 'var(--space-2) var(--space-3)', borderRadius: 0, borderTop: '1px solid var(--rule)', borderLeftWidth: 3, borderLeftStyle: 'solid', fontSize: 'var(--fs-small)', lineHeight: 1.45, whiteSpace: 'normal' },
  indexRowFlagged: { borderLeftColor: 'var(--warning, #8A5A00)' },
  indexRowOk: { borderLeftColor: 'var(--success, #1E7A3D)' },
  indexTitle: { flex: '0 1 auto', fontWeight: 600, color: 'var(--ink)' },
  indexCount: { flex: '0 0 auto', marginLeft: 'auto', fontWeight: 600, color: 'var(--warning, #8A5A00)' },
  indexCountOk: { flex: '0 0 auto', marginLeft: 'auto', color: 'var(--muted, #777)' },
  // THE ROW-ALIGNED GRID. Explicit gridRow on both cells of a pair is what makes the alignment genuine; the row
  // separator + the shared top edge let the eye pair the cells without a heavy spreadsheet look.
  grid: { display: 'grid', columnGap: 'var(--space-4)', rowGap: 0, alignItems: 'start' },
  // DESKTOP/TABLET per-role split (owner: feedback on the RIGHT as its own bounded scroll, CV bullets fixed on the
  // LEFT). Feedback gets the majority width (the working surface) — ~CV 40% / feedback 60%. `alignItems: start` keeps
  // the LEFT CV column pinned to the top instead of stretching to the (possibly taller) right scroll box.
  roleGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 0.66fr) minmax(0, 1fr)', columnGap: 'var(--space-4)', alignItems: 'start' },
  roleLeftCol: { gridColumn: 1, alignSelf: 'start', minWidth: 0 },
  // THE INLINE FEEDBACK SCROLLER — one per role. `max-height` ≈ two feedback items (one fully visible, the next
  // peeking); `overflow-y: auto` + `align-self: start` means a single-item role shrinks to fit (no empty scroll gap,
  // no scrollbar) while a dense role scrolls inside the box. NO `overscroll-behavior` — at the box's end the page
  // scroll takes over normally. This box is NOT an ancestor of the sticky header (that pins on the page), so header
  // pinning is untouched.
  feedbackScroller: { gridColumn: 2, alignSelf: 'start', minWidth: 0, maxHeight: '20rem', overflowY: 'auto', borderTop: '1px solid var(--rule)', paddingRight: 'var(--space-2)' },
  feedbackItem: { position: 'relative', padding: 'var(--space-3) 0', fontSize: 'var(--fs-small)' },
  feedbackItemDivided: { borderTop: '1px solid var(--rule)' },
  feedbackItemActive: { background: 'var(--surface-subtle)', borderRadius: 6 },
  // the LEFT cell — the user's own bullet. Top-aligned with its review, with the gutter mark on the left edge.
  // (longhand borderLeft-* only — mixing the shorthand with the state overrides below causes React style-removal bugs)
  // The unified active-row band (owner amendment 2026-08-04) — one shade, left edge to right edge, gap included.
  // alignSelf stretch is LOAD-BEARING: the grid container top-aligns items (alignItems start), which would
  // collapse this contentless background item to 0px — stretch makes it fill the row track (browser-found).
  rowBand: { position: 'relative', zIndex: 0, background: 'var(--surface-subtle)', borderRadius: 6, pointerEvents: 'none', alignSelf: 'stretch' },
  cellLeft: { position: 'relative', zIndex: 1, alignSelf: 'start', borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: 'transparent', borderTop: '1px solid var(--rule)', padding: 'var(--space-3) 0 var(--space-3) var(--space-2)' },
  cellFlagged: { borderLeftColor: 'var(--warning, #8A5A00)' },
  // an UNFLAGGED (strong) line: left cell only, reduced emphasis, no empty box beside it.
  cellUnflagged: { borderLeftColor: 'var(--success, #1E7A3D)', opacity: 0.7 },
  // The active row's own accent. AMENDED: the SHADE now comes from the row band behind the pair (one uniform
  // highlight across both columns and the gap between them); this cell keeps only the stronger left-edge accent,
  // which still reads as the entry point of the band.
  cellActive: { borderLeftColor: 'var(--ink)', borderLeftWidth: 5 },
  // the RIGHT cell — COMPACT: diagnosis + question + the improve affordance, and nothing else.
  cellRight: { position: 'relative', zIndex: 1, alignSelf: 'start', borderTop: '1px solid var(--rule)', padding: 'var(--space-3) 0', fontSize: 'var(--fs-small)' },
  // stacked (<700px): the feedback sits directly beneath its own bullet, indented under it, still always rendered.
  cellRightStacked: { borderTop: 'none', padding: '0 0 var(--space-3) var(--space-3)', marginLeft: 3, borderLeftWidth: 2, borderLeftStyle: 'solid', borderLeftColor: 'var(--rule)' },
  // ANTI-FABRICATION at the top of the section — prominent, stated ONCE (and again beside any OPEN editor).
  antiFabTop: { margin: '0 0 var(--space-3)', padding: 'var(--space-2) var(--space-3)', borderLeft: '3px solid var(--warning, #8A5A00)', background: 'var(--surface-subtle)', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink)', lineHeight: 1.45 },
  guided: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)', margin: '0 0 var(--space-3)', paddingBottom: 8, borderBottom: '1px solid var(--rule)' },
  guidedNav: { display: 'flex', gap: 'var(--space-1)' },
  guidedBtn: { fontSize: 'var(--fs-small)', marginLeft: 0, paddingLeft: 0 },
  guidedHint: { fontSize: 'var(--fs-label)', color: 'var(--muted, #777)' },
  // R5.7b fix — the one per-entry text link to the facts screen (same quiet label-size idiom as guidedHint).
  entryFactsLink: { margin: 'var(--space-2) 0 0', fontSize: 'var(--fs-label)' },
  // The per-entry details container (2026-08-23): the entry's facts and corrections, on the entry, one click away.
  entryDetails: { margin: 'var(--space-2) 0 0' },
  // a role and its bullets
  role: { borderTop: '1px solid var(--rule)', paddingTop: 12, marginTop: 'var(--space-3)' },
  // SHOW-ALL — the collapsed-entry row: a quiet full-width button in the role slot. Title left, the one-line
  // "N points to review" signal + chevron right; wraps on narrow. No card chrome — the section's --rule hairline
  // (S.role) already separates rows, so the affordance is the chevron + hover from .btn-ghost, kept deliberately calm.
  collapsedRow: { display: 'flex', width: '100%', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-2)', flexWrap: 'wrap' as const, padding: '2px 0', margin: 0, border: 'none', background: 'transparent', textAlign: 'left' as const, cursor: 'pointer', font: 'inherit' },
  collapsedTitle: { fontWeight: 600, fontSize: 'var(--fs-body)', color: 'var(--ink)' },
  collapsedSignal: { fontSize: 'var(--fs-small)', color: 'var(--muted, #777)', whiteSpace: 'nowrap' as const },
  collapsedChevron: { marginLeft: 6, color: 'var(--muted, #777)' },
  roleTitle: { margin: '0 0 var(--space-2)', fontWeight: 600, fontSize: 'var(--fs-body)', color: 'var(--ink)' },
  // The IMG title annotation — the established quiet-note voice (srOtherNote's --fs-label italic --muted), tucked
  // directly under the role title, measure-capped so it reads as a footnote rather than a finding.
  titleAnnotation: { margin: 'calc(-1 * var(--space-1)) 0 var(--space-2)', maxWidth: 'var(--measure)', fontSize: 'var(--fs-label)', fontStyle: 'italic', color: 'var(--muted, #777)', lineHeight: 'var(--lh-body)' },
  strongLead: { margin: 'var(--space-1) 0 2px', fontSize: 'var(--fs-body)', fontWeight: 600, color: 'var(--success, #1E7A3D)' },
  strongMarkers: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--success, #1E7A3D)' },

  bullet: { margin: '0 0 var(--space-4)' },
  bulletText: { margin: '0 0 var(--space-2)', padding: 'var(--space-2) var(--space-3)', background: 'var(--surface-subtle)', color: 'var(--ink)', fontSize: 'var(--fs-body)', lineHeight: 'var(--lh-body)', whiteSpace: 'pre-wrap' },
  bulletList: { margin: '0 0 var(--space-2)', padding: 'var(--space-2) var(--space-3) var(--space-2) var(--space-6)', background: 'var(--surface-subtle)', color: 'var(--ink)', fontSize: 'var(--fs-body)', lineHeight: 'var(--lh-body)' },
  bulletLi: { margin: '0 0 var(--space-1)' },
  bulletLiWeak: { color: 'var(--warning, #8A5A00)' },
  // OWNER RULING 2026-08-08 (compact list) — the PER-LINE active highlight: a quiet background band + ink edge on
  // the active li itself, replacing the old per-cell band inside the compact list.
  bulletLiActive: { background: 'var(--surface-subtle)', boxShadow: 'inset 3px 0 0 var(--ink)', borderRadius: 4 },
  // REGRESSION FIX 2026-08-08 — the atomic single-line paths of the old multi-line look: the per-role number marker
  // and the flagged-point colour (SAME var(--warning) the old weak lines used — that IS the pre-split colour code).
  bulletTextFlagged: { color: 'var(--warning, #8A5A00)' },
  pointNum: { fontWeight: 600, marginRight: 6, fontVariantNumeric: 'tabular-nums' },
  // The feedback card's "Point N" anchor — a QUIET on-theme chip (--muted/--rule), never louder than the diagnosis.
  pointLabel: { display: 'inline-block', margin: '0 0 var(--space-1)', padding: '1px 8px', border: '1px solid var(--rule)', borderRadius: 999, fontSize: 'var(--fs-micro)', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted, #777)' },
  weakTag: { fontSize: 'var(--fs-label)', fontStyle: 'italic', color: 'var(--warning, #8A5A00)' },
  // cc-10 — the disposition chip. Same idiom as `pointLabel` (micro, uppercase, --rule border) so the two read as one
  // system, but with no border fill and one step further back in weight: it introduces the finding, it is not itself
  // the finding. Deliberately NOT coloured by urgency — a label that shouted would compete with the diagnosis.
  dispositionLabel: { display: 'inline-block', margin: '0 0 var(--space-1)', padding: '1px 8px', border: '1px solid var(--rule)', borderRadius: 999, fontSize: 'var(--fs-micro)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted, #777)' },
  assessLine: { margin: '0 0 var(--space-1)', padding: '0 0 0 var(--space-3)', borderLeft: '3px solid var(--warning, #8A5A00)', color: 'var(--ink)', fontSize: 'var(--fs-body)' },
  assessAddressed: { margin: '0 0 var(--space-2)', padding: '0 0 0 var(--space-3)', borderLeft: '3px solid var(--success, #1E7A3D)', color: 'var(--ink-subtle)', fontSize: 'var(--fs-body)' },
  q: { margin: '0 0 var(--space-2)', color: 'var(--ink-subtle)', fontSize: 'var(--fs-body)' },
  // the deterministic quantity escape-route hint (quantityHint.ts) — quiet, clearly product copy, not assessment.
  quantityHint: { margin: 'calc(-1 * var(--space-1)) 0 var(--space-2)', color: 'var(--ink-subtle)', fontSize: 'var(--fs-small)', fontStyle: 'italic' },
  reviseBtn: { fontSize: 'var(--fs-small)', marginLeft: 0, paddingLeft: 0 },
  // the anti-fabrication warning — beside EVERY improve affordance, never once at the top.
  antiFab: { margin: '2px 0 var(--space-2)', fontSize: 'var(--fs-label)', color: 'var(--warning, #8A5A00)', lineHeight: 1.45 },
  reviseNote: { margin: 'var(--space-1) 0 0', fontSize: 'var(--fs-small)', color: 'var(--warning, #8A5A00)' },
  revise: { marginTop: 'var(--space-1)' },
  jog: { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', margin: '0 0 var(--space-2)' },
  chip: { display: 'inline-flex', alignItems: 'center', fontSize: 'var(--fs-small)', padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--rule)', borderRadius: 999, cursor: 'pointer', color: 'var(--ink-subtle)' },
  chipOn: { background: 'var(--surface-subtle)', borderColor: 'var(--ink-subtle)', color: 'var(--ink)' },
  prompt: { margin: '0 0 var(--space-2)' },
  promptLead: { margin: '0 0 2px', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink)' },
  promptList: { margin: 0, paddingLeft: 18, fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)' },
  ta: { width: '100%', padding: 'var(--space-2)', fontFamily: 'inherit', fontSize: 'var(--fs-body)', marginBottom: 'var(--space-2)' },
  save: { fontSize: 'var(--fs-small)' },
  sectionRec: { margin: 'var(--space-1) 0 var(--space-3)', padding: 'var(--space-3) var(--space-3)', borderLeft: '3px solid var(--ink)', background: 'var(--surface-subtle)', fontSize: 'var(--fs-body)', color: 'var(--ink)' },
  // FIX 1 (re-probe) — the read-level "Confirm before you send" panel. Deliberately NOT styled like a section card:
  // no badge, no verdict row, no rating ink — it must not read as a section that acquired a quality verdict.
  confirmPanel: { margin: '0 0 var(--space-4)', padding: 'var(--space-3) var(--space-4)', border: '1px solid var(--border, #DDD)', background: 'var(--surface-subtle)', borderRadius: 6 },
  confirmHeading: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-body)', fontWeight: 600, color: 'var(--ink)' },
  confirmNote: { margin: '0 0 var(--space-3)', fontSize: 'var(--fs-small)', color: 'var(--muted, #555)' },
  // The verify band is PROMINENT (a checkable status/venue claim); the confirm band is calm. The distinction is
  // emphasis only — neither is an adjudicated finding, and the copy in both says so.
  confirmBandVerify: { margin: '0 0 var(--space-3)', paddingLeft: 'var(--space-3)', borderLeft: '3px solid var(--amber, #B7791F)' },
  confirmBandCalm: { margin: '0 0 var(--space-2)', paddingLeft: 'var(--space-3)', borderLeft: '3px solid var(--ink-subtle)' },
  confirmBandHeading: { margin: '0 0 var(--space-1)', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink)' },
  confirmBandNote: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--muted, #555)' },
  confirmSectionLabel: { margin: 'var(--space-2) 0 var(--space-1)', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--muted, #555)' },
  confirmEntry: { margin: '0 0 var(--space-2)' },
  confirmEntryTitle: { margin: '0 0 var(--space-1)', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink)' },
  confirmLine: { margin: '0 0 var(--space-1)', padding: 'var(--space-2)', background: 'var(--surface)', borderRadius: 4, fontSize: 'var(--fs-small)' },
  unreadablePanel: { margin: '0 0 var(--space-4)', padding: 'var(--space-3) var(--space-4)', borderLeft: '3px solid var(--amber, #B7791F)', background: 'var(--surface-subtle)', borderRadius: 6 },
  unreadableHeading: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-body)', fontWeight: 600, color: 'var(--ink)' },
  unreadableRow: { margin: '0 0 var(--space-1)', fontSize: 'var(--fs-small)', color: 'var(--muted, #555)' },
  editorial: { margin: 'var(--space-2) 0 0', padding: 'var(--space-2) var(--space-3)', borderLeft: '3px solid var(--ink-subtle)', background: 'var(--surface-subtle)', fontSize: 'var(--fs-small)', color: 'var(--muted, #555)' },
  staleGate: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap', margin: 'var(--space-2) 0 var(--space-4)', padding: 'var(--space-3) var(--space-4)', borderRadius: 8, border: '1px solid var(--amber, #B7791F)', background: 'var(--surface-subtle)', fontSize: 'var(--fs-body)', color: 'var(--ink)' },
  // Re-run VISIBLE ACTIVITY
  srOnly: { position: 'absolute', width: 1, height: 1, margin: -1, padding: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 },
  updatedNote: { margin: '0 0 var(--space-4)', fontSize: 'var(--fs-small)', color: 'var(--success, #1E7A3D)' },
  readBody: { position: 'relative', transition: 'opacity 160ms ease' },
  readBodyBusy: { opacity: 0.55 },
  busyStripTrack: { position: 'relative', height: 4, marginBottom: 'var(--space-3)', borderRadius: 999, background: 'var(--surface-subtle)', overflow: 'hidden' },
  busyStripBar: { position: 'absolute', top: 0, bottom: 0, width: '40%', borderRadius: 999, background: 'var(--ink-subtle, #486581)', animation: 'cvc-indeterminate 1.4s linear infinite' },
  busyStill: { display: 'block', fontSize: 'var(--fs-micro)', lineHeight: '4px', color: 'var(--muted, #777)' },
  spinner: { display: 'inline-block', width: 12, height: 12, marginRight: 6, verticalAlign: -1, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', animation: 'cvc-spin 0.8s linear infinite' },
  spinnerStill: { display: 'inline-block', width: 12, height: 12, marginRight: 6, verticalAlign: -1, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent' },
  parseWarn: { margin: '0 0 var(--space-4)', padding: 'var(--space-4) var(--space-4)', borderRadius: 8, border: '1px solid var(--amber, #B7791F)', background: 'var(--surface-subtle)', fontSize: 'var(--fs-body)', color: 'var(--ink)', lineHeight: 'var(--lh-body)' },
  parseWarnTitle: { margin: '0 0 var(--space-2)', fontSize: 17, color: 'var(--ink)' },
  reviewedAt: { fontSize: 'var(--fs-label)', color: 'var(--muted, #777)' },
  rerunGhost: { fontSize: 'var(--fs-small)' },
  // L3 — the detail layer
  detailLayer: { marginTop: 'var(--space-4)' },
  detail: { margin: '0 0 var(--space-2)', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--rule)', borderRadius: 6, background: 'var(--card)' },
  detailSummary: { cursor: 'pointer', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink-subtle)' },
  detailBody: { margin: 'var(--space-2) 0 0', maxWidth: 'var(--measure)', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)', lineHeight: 'var(--lh-body)' },
  // Specialty relevance — the chip row
  srPanel: { margin: '0 0 var(--space-5)', padding: 'var(--space-4) var(--space-4)', border: '1px solid var(--rule)', borderRadius: 8, background: 'var(--card)' },
  srHeading: { margin: '0 0 var(--space-1)', fontSize: 17, color: 'var(--ink)' },
  srSubline: { margin: '0 0 var(--space-3)', fontSize: 'var(--fs-small)', color: 'var(--muted, #777)', lineHeight: 'var(--lh-body)' },
  srChips: { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', margin: '0 0 var(--space-3)' },
  srChip: { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--fs-small)', padding: 'var(--space-1) var(--space-3)', border: '1px solid var(--rule)', borderRadius: 999, background: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink-subtle)' },
  srChipOn: { background: 'var(--surface-subtle)', borderColor: 'var(--ink-subtle)', color: 'var(--ink)', fontWeight: 600 },
  srSignal: { margin: '0 0 var(--space-3)', paddingBottom: 10, borderBottom: '1px solid var(--rule)' },
  srSignalLine: { margin: '0 0 var(--space-1)', fontSize: 'var(--fs-body)', fontWeight: 600, color: 'var(--success, #1E7A3D)' },
  srWhatCounts: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)' },
  srAdjacent: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', margin: '0 0 2px', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)' },
  srHideBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #777)', fontSize: 'var(--fs-body)', lineHeight: 1, padding: '0 var(--space-1)' },
  srOther: { marginTop: 'var(--space-2)' },
  srOtherSummary: { cursor: 'pointer', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink-subtle)' },
  srOtherNote: { margin: 'var(--space-2) 0', fontSize: 'var(--fs-label)', fontStyle: 'italic', color: 'var(--muted, #777)' },
  srOtherItem: { margin: '0 0 var(--space-3)', paddingLeft: 10, borderLeft: '3px solid var(--rule)' },
  srOtherPlain: { margin: '0 0 2px', fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--ink)' },
  srOtherRecover: { margin: '0 0 2px', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)' },
  srOtherBasis: { margin: 0, fontSize: 'var(--fs-label)', color: 'var(--muted, #777)' },
  srUpgradeOffer: { margin: '0 0 var(--space-4)' },
  srUpgradeBtn: { fontSize: 'var(--fs-small)', marginLeft: 0, paddingLeft: 0 },
};
